
import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { AuthContext } from "../../utils/AuthContext";
import CategoryCubes from "../../components/CategoryCubes";
import { EPICS_DATA } from "../../utils/EpicsData";
import ButtonPlus from "../../components/ButtonPlus";
import Modal from '../../components/Modal';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Search from '../../components/Search';
import KeyboardShortcut from '../../components/KeyboardShortcut';
import useKey from "../../utils/KeyListener";
import branchIcon from "../../imgs/branch.png";

// Extracted modules
import { workIndicatorStyle } from "./Project/projectStyles";
import { cleanDodStatus } from "./Project/utils/dodUtils";
import { calculateMatchScore } from "./Project/utils/taskUtils";
import SuggestedComponents from "./Project/components/SuggestedComponents";
import CategoryModal from "./Project/components/CategoryModal";
import SubEpicDoDSection from "./Project/components/SubEpicDoDSection";
import SubEpicTaskList from "./Project/components/SubEpicTaskList";
import { useWorkItems } from "./Project/hooks/useWorkItems";
import { useUsers } from "./Project/hooks/useUsers";
import { useDisciplines } from "./Project/hooks/useDisciplines";

// TaskItem component has been extracted to ./components/TaskItem.js

export default function Project() {
  const { supabase, user, refresh } = useContext(AuthContext);
  const { workItems, setWorkItems } = useWorkItems(supabase, user);
  const { users } = useUsers(supabase, user);
  const { disciplines, doDsByDiscipline } = useDisciplines(supabase, user);
  
  // Inject CSS for work indicator animation
  useEffect(() => {
    const styleId = 'work-indicator-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = workIndicatorStyle;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Helper function to check if sub-epic's correlating story is not completed
  const hasActiveTasks = useCallback((subEpicId) => {
    if (!workItems || !subEpicId) return false;
    
    // Get all tasks directly under this sub-epic
    const tasks = workItems.filter(w => 
      w.parent_id === subEpicId && w.type === 'task'
    );
    
    // If no tasks, don't show indicator
    if (tasks.length === 0) return false;
    
    // If all tasks have no story_id, don't show indicator
    if (tasks.every(task => !task.story_id)) {
      return false;
    }
    
    // Get unique story IDs from these tasks
    const storyIds = [...new Set(tasks.map(task => task.story_id).filter(Boolean))];
    
    // Check if any of the correlating stories are NOT completed
    return storyIds.some(storyId => {
      const story = workItems.find(w => w.id === storyId && w.type === 'story');
      // Show indicator if story doesn't exist, or if story exists and is not completed
      return !story || story.status !== 'COMPLETE';
    });
  }, [workItems]);
  const [desc, setDesc] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryDraft, setCategoryDraft] = useState("");
  const [modalCategory, setModalCategory] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDesc, setModalDesc] = useState("");
  const [modalError, setModalError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastAddedEpicTitle, setLastAddedEpicTitle] = useState(null);
  const [hoveredSuggestionPanel, setHoveredSuggestionPanel] = useState(false);
  const [editingChildId, setEditingChildId] = useState(null);
  const [childDraft, setChildDraft] = useState("");
  const [modalChild, setModalChild] = useState(null);
  const [modalChildTitle, setModalChildTitle] = useState("");
  const [modalChildDesc, setModalChildDesc] = useState("");
  const [modalChildError, setModalChildError] = useState("");
  const modalChildTitleInputRef = useRef(null);
  const descriptionTextareaRef = useRef(null);
  const descriptionSaveTimerRef = useRef(null);
  const handleCreateNewTaskRef = useRef(null);
  const justDraggedSubEpicRef = useRef(false);
  const [modalChildDoDStatuses, setModalChildDoDStatuses] = useState({});
  const [modalChildDoDEditMode, setModalChildDoDEditMode] = useState(false);
  const [editDisciplinesButtonHovered, setEditDisciplinesButtonHovered] = useState(false);
  // Disciplines and DoDs are now provided by useDisciplines hook
  const [childAreaHovered, setChildAreaHovered] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [subEpicHovered, setSubEpicHovered] = useState(null); // only setSubEpicHovered is used
  const [breakdownButtonHovered, setBreakdownButtonHovered] = useState(null);
  // Breakdown functionality in modal state
  const [modalChildTasks, setModalChildTasks] = useState([]);
  const [showBreakdownOptions, setShowBreakdownOptions] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [editingTaskField, setEditingTaskField] = useState(null); // 'title', 'estimate', 'assignee', 'status', 'story'
  // Task selection and search in modal
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(-1);
  const [modalSearchVisible, setModalSearchVisible] = useState(false);
  const [modalSearchText, setModalSearchText] = useState("");
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [breakdownStories, setBreakdownStories] = useState([]); // only setBreakdownStories is used
  const [allStories, setAllStories] = useState([]); // All stories for task editing
  // eslint-disable-next-line no-unused-vars
  const [breakdownSelectedStory, setBreakdownSelectedStory] = useState(null); // only setBreakdownSelectedStory is used
  const [breakdownNewStoryName, setBreakdownNewStoryName] = useState("");
  const [breakdownSelectedOption, setBreakdownSelectedOption] = useState(0); // 0 = new story, 1 = existing story
  const [breakdownSelectedStoryId, setBreakdownSelectedStoryId] = useState(null); // ID of selected story, null means create new, -1 means no story
  const [showStoryDropdown, setShowStoryDropdown] = useState(false);
  const [storyInputValue, setStoryInputValue] = useState(""); // For the story combo input
  // eslint-disable-next-line no-unused-vars
  const [isTypingStory, setIsTypingStory] = useState(false); // Track if user is actively typing
  const [noStorySelected, setNoStorySelected] = useState(false); // Allow creating tasks without stories
  const storyDropdownRef = useRef(null);
  // Remove descSubmitted from state
  // const [descSubmitted, setDescSubmitted] = useState(false);
  
  // Users are now provided by useUsers hook
  // Estimate, assignee, and status for new task creation (used by BreakdownForm)
  const [breakdownTaskEstimate, setBreakdownTaskEstimate] = useState(null);
  const [breakdownTaskAssignee, setBreakdownTaskAssignee] = useState(null);
  const [breakdownTaskStatus, setBreakdownTaskStatus] = useState('TODO');
  const breakdownOptionsRef = useRef(null);
  
  // Search functionality state
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [highlightedItem, setHighlightedItem] = useState(null);

  // Keyboard navigation state
  const [selectedCategory, setSelectedCategory] = useState(-1);
  const [selectedSubcategory, setSelectedSubcategory] = useState(-1);
  
  // Mouse position tracking for hover detection
  const mousePositionRef = useRef({ x: 0, y: 0 });
  const hoverStateRef = useRef({});
  const [buttonHovered, setButtonHovered] = useState({});
  const [addCategoryButtonHovered, setAddCategoryButtonHovered] = useState(false);
  
  // Ref for description textarea auto-resize
  const descTextareaRef = useRef(null);
  
  // State for 3D cube highlighting
  const [highlightedCategoryId, setHighlightedCategoryId] = useState(null);
  
  // 3D cube click handler
  const handleCubeClick = () => {
    // Simple click interaction - could add some visual feedback here
    console.log('Cube clicked!');
  };

  // Work items, users, and disciplines are now provided by hooks

  // Fetch children tasks when modal opens
  useEffect(() => {
    async function fetchChildrenTasks() {
      if (!supabase || !user?.proj?.id || !modalChild) return;
      
      // Fetch children tasks directly under this sub-epic
      const { data: childTasks } = await supabase
        .from("work")
        .select("id, title, story_id, owner_id, estimate, status, child_order")
        .eq("project_id", user.proj.id)
        .eq("parent_id", modalChild.id)
        .eq("type", "task")
        .order("title", { ascending: true });
      
      // Fetch all stories to get story titles for tasks
      const { data: fetchedStories } = await supabase
        .from("work")
        .select("id, title, status")
        .eq("project_id", user.proj.id)
        .eq("type", "story")
        .is("parent_id", null)
        .order("title", { ascending: true });
      
      // Set allStories to include ALL stories (including completed) 
      // This allows us to find the story title for tasks that might be assigned to completed stories
      // The dropdown list will filter to show only non-completed stories
      setAllStories(fetchedStories || []);
      
      // Filter out completed stories for breakdown (new task creation)
      const nonCompletedStories = (fetchedStories || []).filter(story => story.status !== 'COMPLETE');
      setBreakdownStories(nonCompletedStories);
      
      // Add story information to each task
      let tasksWithStories = (childTasks || []).map(task => {
        const story = fetchedStories?.find(s => s.id === task.story_id);
        return {
          ...task,
          storyTitle: story?.title || null
        };
      });
      
      // Order tasks by child_order if it exists on the parent subepic
      if (modalChild?.child_order && Array.isArray(modalChild.child_order) && modalChild.child_order.length > 0) {
        const orderedTasks = [];
        const unorderedTasks = [];
        
        modalChild.child_order.forEach(taskId => {
          const task = tasksWithStories.find(t => t.id === taskId);
          if (task) orderedTasks.push(task);
        });
        
        tasksWithStories.forEach(task => {
          if (!modalChild.child_order.includes(task.id)) {
            unorderedTasks.push(task);
          }
        });
        
        tasksWithStories = [...orderedTasks, ...unorderedTasks];
      }
      
      setModalChildTasks(tasksWithStories);
      
      // Fetch all standalone stories for breakdown dropdown
      // Stories are standalone (parent_id = null) and can belong to any goal or no goal
      const { data: breakdownStoriesData, error: storiesError } = await supabase
        .from("work")
        .select("id, title, goal_id, child_order, status")
        .eq("project_id", user.proj.id)
        .eq("type", "story")
        .is("parent_id", null)
        .order("title", { ascending: true });
      
      if (storiesError) {
        console.error("Error fetching breakdown stories:", storiesError);
      }
      
      // Filter out completed stories for breakdown
      const nonCompletedBreakdownStories = (breakdownStoriesData || []).filter(story => story.status !== 'COMPLETE');
      setBreakdownStories(nonCompletedBreakdownStories);
      
      // Load preference from localStorage
      const savedPreference = localStorage.getItem('breakdownOptionPreference');
      if (savedPreference !== null) {
        setBreakdownSelectedOption(parseInt(savedPreference, 10));
      }
      // Set initial task name to blank
      setBreakdownNewStoryName("");
      setStoryInputValue("");
      setNoStorySelected(false);
      setBreakdownSelectedStoryId(null);
      // Story field remains unpopulated - user must manually select a story if desired
    }
    if (modalChild) {
      fetchChildrenTasks();
      // Don't reset breakdown options if we're opening with breakdown - it will be set by openEditModal
      // Only reset if modal is being opened without breakdown
      if (!showBreakdownOptions) {
      setShowBreakdownOptions(false);
        setShowStoryDropdown(false);
      }
      // Reset task selection when modal opens
      setSelectedTaskIndex(-1);
      setHighlightedTaskId(null);
    } else {
      // Reset task selection when modal closes
      setSelectedTaskIndex(-1);
      setHighlightedTaskId(null);
      setModalSearchVisible(false);
      setModalSearchText("");
    }
  }, [supabase, user, modalChild, showBreakdownOptions]);

  // Close story dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (storyDropdownRef.current && !storyDropdownRef.current.contains(event.target)) {
        setShowStoryDropdown(false);
      }
    };

    if (showStoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStoryDropdown]);

  // Blur story field when a story is selected (handled inside BreakdownForm component)

  // Close breakdown options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (breakdownOptionsRef.current && !breakdownOptionsRef.current.contains(event.target)) {
        // If task has a title, create it instead of canceling
        if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
          if (handleCreateNewTaskRef.current) {
            handleCreateNewTaskRef.current();
          }
          return;
        }
        // Reset breakdown options state
        setShowBreakdownOptions(false);
        setBreakdownNewStoryName("");
        setStoryInputValue("");
        setNoStorySelected(false);
        setBreakdownSelectedStoryId(null);
        setBreakdownTaskEstimate(null);
        setBreakdownTaskAssignee(null);
        setBreakdownTaskStatus('TODO');
      }
    };

    if (showBreakdownOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBreakdownOptions, breakdownNewStoryName]);

  // Save preference to localStorage when it changes
  useEffect(() => {
    if (showBreakdownOptions) {
      localStorage.setItem('breakdownOptionPreference', breakdownSelectedOption.toString());
    }
  }, [breakdownSelectedOption, showBreakdownOptions]);

  // Disciplines loading is now handled by useDisciplines hook

  // Mouse move handler for hover detection
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Update mouse position ref
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
      
      // Only check hover when subcategories exist
      if (workItems.filter(w => w.type === 'subepic' && w.parent_id != null).length === 0) return;
      
      // Check if mouse is over suggestions overlay - if so, skip hover detection
      const suggestionsOverlay = document.querySelector('[data-suggestions-overlay]');
      if (suggestionsOverlay) {
        const overlayRect = suggestionsOverlay.getBoundingClientRect();
        if (e.clientY >= overlayRect.top) {
          // Mouse is in suggestions overlay area, don't update hover states
          return;
        }
      }
      
      const categories = workItems.filter(w => w.type === 'epic' && w.parent_id == null);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const topBoundary = 200; // 200px from top
      
      // Calculate bottom boundary - exclude suggestions overlay area
      const bottomBoundary = suggestionsOverlay ? suggestionsOverlay.getBoundingClientRect().top : viewportHeight;
      
      // Get all category elements
      const categoryElements = document.querySelectorAll('[data-category-id]');
      
      let hasChanges = false;
      const newHoverState = {};
      
      categories.forEach((item, i) => {
        const categoryElement = Array.from(categoryElements).find(el => el.dataset.categoryId === item.id.toString());
        if (!categoryElement) return;
        
        const rect = categoryElement.getBoundingClientRect();
        const isFirstCategory = i === 0;
        const isLastCategory = i === categories.length - 1;
        
        let isInHoverArea = false;
        
        if (isFirstCategory) {
          // Leftmost category: from left edge of screen to right edge of category
          isInHoverArea = e.clientX >= 0 && e.clientX <= rect.right && e.clientY >= topBoundary && e.clientY <= bottomBoundary;
        } else if (isLastCategory) {
          // Rightmost category: from left edge of category to right edge of viewport
          isInHoverArea = e.clientX >= rect.left && e.clientX <= viewportWidth && e.clientY >= topBoundary && e.clientY <= bottomBoundary;
        } else {
          // Middle categories: from left edge of previous category to right edge of next category
          const prevCategoryElement = Array.from(categoryElements).find(el => el.dataset.categoryId === categories[i-1]?.id.toString());
          const nextCategoryElement = Array.from(categoryElements).find(el => el.dataset.categoryId === categories[i+1]?.id.toString());
          
          const leftBound = prevCategoryElement ? prevCategoryElement.getBoundingClientRect().right : 0;
          const rightBound = nextCategoryElement ? nextCategoryElement.getBoundingClientRect().left : viewportWidth;
          
          isInHoverArea = e.clientX >= leftBound && e.clientX <= rightBound && e.clientY >= topBoundary && e.clientY <= bottomBoundary;
        }
        
        newHoverState[item.id] = isInHoverArea;
        
        // Only update state if there's a change
        if (hoverStateRef.current[item.id] !== isInHoverArea) {
          hasChanges = true;
        }
      });
      
      // Only update state if there are changes
      if (hasChanges) {
        hoverStateRef.current = newHoverState;
        setChildAreaHovered(newHoverState);
      }
    };

    const handleMouseOut = (e) => {
      // Hide all add buttons when mouse leaves window
      if (!e.relatedTarget) {
        const categories = workItems.filter(w => w.type === 'epic' && w.parent_id == null);
        const newHoverState = {};
        categories.forEach(item => {
          newHoverState[item.id] = false;
        });
        hoverStateRef.current = newHoverState;
        setChildAreaHovered(newHoverState);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseout', handleMouseOut);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [workItems]);

  // Set suggestions from stored project type on load
  useEffect(() => {
    const allowedKeys = [
      'video_game',
      'mobile_web_app',
      'saas_platform',
      'digital_content_platform',
      'ai_ml_product'
    ];
    const typeKey = (user?.proj?.type || '').trim().toLowerCase();
    if (allowedKeys.includes(typeKey)) {
      setSuggested(EPICS_DATA[typeKey]?.epics || []);
    } else {
      setSuggested([]);
    }
  }, [user?.proj?.type]);

  // Sync desc state with project description from user context
  useEffect(() => {
    if (user?.proj?.description && !desc) {
      setDesc(user.proj.description);
    }
  }, [user, desc]);

  // Save description to database
  const saveDescription = useCallback(async () => {
    if (!modalChild || !modalChild.id) return;
    try {
      await supabase.from('work').update({ description: modalChildDesc }).eq('id', modalChild.id);
      setWorkItems(ws => ws.map(w => w.id === modalChild.id ? { ...w, description: modalChildDesc } : w));
    } catch (error) {
      console.error('Error saving description:', error);
    }
  }, [modalChild, modalChildDesc, supabase, setWorkItems]);



  // Debounced save for description (3 seconds after typing stops)
  useEffect(() => {
    if (modalChild && modalChild.id) {
      // Clear existing timer
      if (descriptionSaveTimerRef.current) {
        clearTimeout(descriptionSaveTimerRef.current);
      }
      // Set new timer
      descriptionSaveTimerRef.current = setTimeout(() => {
        saveDescription();
      }, 3000);
      // Cleanup on unmount or when modalChild changes
      return () => {
        if (descriptionSaveTimerRef.current) {
          clearTimeout(descriptionSaveTimerRef.current);
        }
      };
    }
  }, [modalChildDesc, modalChild, saveDescription]);

  // Save description when modal closes
  useEffect(() => {
    return () => {
      // Cleanup function runs when modalChild becomes null
      if (descriptionSaveTimerRef.current) {
        clearTimeout(descriptionSaveTimerRef.current);
      }
    };
  }, [modalChild]);

  // Auto-resize description textarea
  useEffect(() => {
    if (editingDesc && descTextareaRef.current) {
      const textarea = descTextareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [editingDesc, descDraft]);

  // Keyboard navigation logic - define categories and getSubcategories before search
  const categories = workItems.filter(w => w.type === 'epic' && w.parent_id == null);
  
  // Get subcategories for the currently selected category
  const getSubcategories = useCallback((categoryIndex) => {
    if (categoryIndex < 0 || categoryIndex >= categories.length) return [];
    const category = categories[categoryIndex];
    const subepics = workItems.filter(w => w.parent_id === category.id && w.type === 'subepic');
    
    // If epic has child_order, use it to order subepics
    if (category.child_order && Array.isArray(category.child_order) && category.child_order.length > 0) {
      const ordered = [];
      const unordered = [];
      
      category.child_order.forEach(subepicId => {
        const subepic = subepics.find(s => s.id === subepicId);
        if (subepic) ordered.push(subepic);
      });
      
      subepics.forEach(subepic => {
        if (!category.child_order.includes(subepic.id)) {
          unordered.push(subepic);
        }
      });
      
      return [...ordered, ...unordered];
    }
    
    return subepics;
  }, [workItems, categories]);

  // Search functionality
  const handleSearch = useCallback((text) => {
    if (!text.trim()) {
      setHighlightedItem(null);
      setSelectedCategory(-1);
      setSelectedSubcategory(-1);
      return;
    }


    const searchLower = text.toLowerCase();
    let bestMatch = null;
    let bestScore = -1;
    let bestCategoryIndex = -1;
    let bestSubcategoryIndex = -1;

    // Search in categories (use the categories defined above)
    categories.forEach((category, categoryIndex) => {
      if (category.title) {
        const score = calculateMatchScore(category.title, searchLower);
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = { type: 'category', id: category.id, title: category.title };
          bestCategoryIndex = categoryIndex;
          bestSubcategoryIndex = -1;
        }
      }

      // Search in subcategories - use getSubcategories to match the rendering order
      const subcategories = getSubcategories(categoryIndex);
      subcategories.forEach((subcategory, subcategoryIndex) => {
        if (subcategory.title) {
          const score = calculateMatchScore(subcategory.title, searchLower);
          
          if (score > bestScore) {
            bestScore = score;
            bestMatch = { type: 'subcategory', id: subcategory.id, title: subcategory.title };
            bestCategoryIndex = categoryIndex;
            bestSubcategoryIndex = subcategoryIndex;
          }
        }
      });
    });

    setHighlightedItem(bestMatch);
    setSelectedCategory(bestCategoryIndex);
    setSelectedSubcategory(bestSubcategoryIndex);
  }, [categories, getSubcategories]);

  // Search functionality for tasks in modal
  const handleModalSearch = useCallback((text) => {
    if (!text.trim()) {
      setHighlightedTaskId(null);
      setSelectedTaskIndex(-1);
      return;
    }

    const searchLower = text.toLowerCase();
    let bestMatch = null;
    let bestScore = -1;
    let bestTaskIndex = -1;

    // Search in tasks
    modalChildTasks.forEach((task, taskIndex) => {
      if (task.title) {
        const score = calculateMatchScore(task.title, searchLower);
        
        if (score > bestScore) {
          bestScore = score;
          bestMatch = task;
          bestTaskIndex = taskIndex;
        }
      }
    });

    if (bestMatch) {
      setHighlightedTaskId(bestMatch.id);
      setSelectedTaskIndex(bestTaskIndex);
    } else {
      setHighlightedTaskId(null);
      setSelectedTaskIndex(-1);
    }
  }, [modalChildTasks]);

  // Call search handler when search text changes
  useEffect(() => {
    if (modalSearchVisible && modalSearchText) {
      handleModalSearch(modalSearchText);
    }
  }, [modalSearchText, modalSearchVisible, handleModalSearch]);

  // calculateMatchScore is now imported from ./utils/taskUtils


  // Remove the debounced OpenAI useEffect for desc

  // Navigation functions
  const navigateUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedCategory === -1) {
      // No selection, select first category
      if (categories.length > 0) {
        setSelectedCategory(0);
        setSelectedSubcategory(-1);
      }
    } else if (selectedSubcategory > -1) {
      // Navigate between subcategories
      if (selectedSubcategory > 0) {
        setSelectedSubcategory(selectedSubcategory - 1);
      } else {
        // Move to category level
        setSelectedSubcategory(-1);
      }
    }
    // If at category level, don't move to previous category - just stay at category level
  };

  const navigateDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedCategory === -1) {
      // No selection, select first category
      if (categories.length > 0) {
        setSelectedCategory(0);
        setSelectedSubcategory(-1);
      }
    } else if (selectedSubcategory > -1) {
      // Navigate between subcategories
      const subcategories = getSubcategories(selectedCategory);
      if (selectedSubcategory < subcategories.length - 1) {
        setSelectedSubcategory(selectedSubcategory + 1);
      }
    } else if (selectedCategory > -1) {
      // Move to subcategory level
      const subcategories = getSubcategories(selectedCategory);
      if (subcategories.length > 0) {
        setSelectedSubcategory(0);
      }
    }
  };

  const navigateLeft = () => {
    if (selectedCategory === -1) {
      // No selection, select first category
      if (categories.length > 0) {
        setSelectedCategory(0);
        setSelectedSubcategory(-1);
      }
    } else if (selectedSubcategory > -1) {
      // Navigate to adjacent parent category and closest matching subcategory
      if (selectedCategory > 0) {
        const newCategoryIndex = selectedCategory - 1;
        const targetSubcategories = getSubcategories(newCategoryIndex);
        
        if (targetSubcategories.length > 0) {
          // Select the closest available subcategory
          const closestIndex = Math.min(selectedSubcategory, targetSubcategories.length - 1);
          setSelectedCategory(newCategoryIndex);
          setSelectedSubcategory(closestIndex);
        } else {
          // If target has no subcategories, select the category itself
          setSelectedCategory(newCategoryIndex);
          setSelectedSubcategory(-1);
        }
      }
    } else if (selectedCategory > 0) {
      // Navigate between categories
      setSelectedCategory(selectedCategory - 1);
      setSelectedSubcategory(-1);
    }
  };

  const navigateRight = () => {
    if (selectedCategory === -1) {
      // No selection, select first category
      if (categories.length > 0) {
        setSelectedCategory(0);
        setSelectedSubcategory(-1);
      }
    } else if (selectedSubcategory > -1) {
      // Navigate to adjacent parent category and closest matching subcategory
      if (selectedCategory < categories.length - 1) {
        const newCategoryIndex = selectedCategory + 1;
        const targetSubcategories = getSubcategories(newCategoryIndex);
        
        if (targetSubcategories.length > 0) {
          // Select the closest available subcategory
          const closestIndex = Math.min(selectedSubcategory, targetSubcategories.length - 1);
          setSelectedCategory(newCategoryIndex);
          setSelectedSubcategory(closestIndex);
        } else {
          // If target has no subcategories, select the category itself
          setSelectedCategory(newCategoryIndex);
          setSelectedSubcategory(-1);
        }
      }
    } else if (selectedCategory < categories.length - 1) {
      // Navigate between categories
      setSelectedCategory(selectedCategory + 1);
      setSelectedSubcategory(-1);
    }
  };

  const openEditModal = (withBreakdown = false, specificChild = null) => {
    if (editingChildId) return;
    
    // If a specific child is provided, use it directly (e.g., from breakdown button)
    if (specificChild) {
      setModalChild(specificChild);
      setModalChildTitle(specificChild.title);
      setModalChildDesc(specificChild.description || "");
      // Parse dod_status JSON or default to empty object
      try {
        const dodStatuses = specificChild.dod_status ? JSON.parse(specificChild.dod_status) : {};
        const cleanedStatuses = cleanDodStatus(dodStatuses, disciplines);
        setModalChildDoDStatuses(cleanedStatuses);
      } catch (e) {
        // If parsing fails, default to empty object
        setModalChildDoDStatuses({});
      }
      setModalChildDoDEditMode(false);
      setModalChildError("");
      // If withBreakdown is true, show breakdown options and auto-create task field
      if (withBreakdown) {
        setShowBreakdownOptions(true);
        setBreakdownNewStoryName(""); // Start with blank task name
        setStoryInputValue(""); // Start with blank story name
        setBreakdownTaskEstimate(null);
        setBreakdownTaskAssignee(null);
        setBreakdownTaskStatus('TODO');
        setNoStorySelected(false);
        setBreakdownSelectedStoryId(null);
      }
      return;
    }
    
    if (selectedCategory === -1) {
      // No selection, just select first category, don't open modal
      if (categories.length > 0) {
        setSelectedCategory(0);
        setSelectedSubcategory(-1);
      }
      } else if (selectedSubcategory > -1) {
      // Open subcategory edit modal
      const subcategories = getSubcategories(selectedCategory);
      if (subcategories[selectedSubcategory]) {
        const child = subcategories[selectedSubcategory];
        setModalChild(child);
        setModalChildTitle(child.title);
        setModalChildDesc(child.description || "");
        // Parse dod_status JSON or default to empty object
        try {
          const dodStatuses = child.dod_status ? JSON.parse(child.dod_status) : {};
          const cleanedStatuses = cleanDodStatus(dodStatuses, disciplines);
          setModalChildDoDStatuses(cleanedStatuses);
        } catch (e) {
          // If parsing fails, default to empty object
          setModalChildDoDStatuses({});
        }
        setModalChildDoDEditMode(false);
        setModalChildError("");
        // If withBreakdown is true, show breakdown options and auto-create task field
        if (withBreakdown) {
          setShowBreakdownOptions(true);
          setBreakdownNewStoryName(""); // Start with blank task name
          setStoryInputValue(""); // Start with blank story name
          setNoStorySelected(false);
          setBreakdownSelectedStoryId(null);
        }
      } else {
        // Subcategory no longer exists, reset selection
        setSelectedCategory(-1);
        setSelectedSubcategory(-1);
      }
    } else if (selectedCategory > -1) {
      // Open category edit modal
      const category = categories[selectedCategory];
      if (category) {
        setModalCategory(category);
        setModalTitle(category.title);
        setModalDesc(category.description || "");
        setModalError("");
      } else {
        // Category no longer exists, reset selection
        setSelectedCategory(-1);
        setSelectedSubcategory(-1);
      }
    }
  };

  // Keyboard event handlers
  useKey("ArrowUp", false, (e) => {
    if (editingDesc || modalCategory || modalChild) return;
    navigateUp(e);
  });
  useKey("ArrowDown", false, (e) => {
    if (editingDesc || modalCategory || modalChild) return;
    navigateDown(e);
  });
  useKey("ArrowLeft", false, (e) => {
    if (editingDesc || modalCategory || modalChild) return;
    navigateLeft();
  }, null);
  useKey("ArrowRight", false, (e) => {
    if (editingDesc || modalCategory || modalChild) return;
    navigateRight();
  }, null);
  useKey("Enter", false, (e) => {
    // Don't do anything if editing description - let the textarea handle it
    if (editingDesc) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // If search is visible, just close it without opening modal or clearing highlight
    if (searchVisible) {
      e.preventDefault();
      e.stopPropagation();
      setSearchVisible(false);
      setSearchText("");
      typingBufferRef.current = "";
      return;
    }
    // Don't open modal if a modal is already open
    if (modalCategory || modalChild) return;
    openEditModal();
  }, null);
  useKey("Escape", false, (e) => {
    if (editingDesc) {
      e.preventDefault();
      e.stopPropagation();
      setEditingDesc(false);
      setDescDraft(user.proj.description);
      return;
    }
    // If modal is open, check if a TaskItem is in edit mode or showing all fields
    if (modalChild) {
      // Check if breakdown form is visible (task creation in progress)
      const isBreakdownFormVisible = showBreakdownOptions;
      const isWithinBreakdownForm = breakdownOptionsRef.current && breakdownOptionsRef.current.contains(document.activeElement);
      
      // If breakdown form is visible or active element is within it, handle task creation
      if (isBreakdownFormVisible || isWithinBreakdownForm) {
        e.preventDefault();
        e.stopPropagation();
        // If task has a title, create it instead of canceling
        if (breakdownNewStoryName && breakdownNewStoryName.trim()) {
          if (handleCreateNewTaskRef.current) {
            handleCreateNewTaskRef.current();
          }
          return;
        }
        // Cancel task creation - reset all fields and hide the form
        setShowBreakdownOptions(false);
        setBreakdownNewStoryName("");
        setStoryInputValue("");
        setNoStorySelected(false);
        setBreakdownSelectedStoryId(null);
        setBreakdownTaskEstimate(null);
        setBreakdownTaskAssignee(null);
        setBreakdownTaskStatus('TODO');
        return;
      }
      
      // Check if any TaskItem is in edit mode
      const isTaskItemEditing = editingTaskId !== null;
      
      // Check if the active element is within a TaskItem (indicates showAllFields might be true or field is focused)
      const activeElement = document.activeElement;
      const isWithinTaskItem = activeElement?.closest('[data-task-id]') !== null;
      
      // Check if any TaskItem has showAllFields set to true
      const hasTaskItemShowingAllFields = document.querySelector('[data-task-id][data-show-all-fields="true"]') !== null;
      
      // If a TaskItem is in edit mode or showing all fields, reset it instead of closing modal
      if (isTaskItemEditing || isWithinTaskItem || hasTaskItemShowingAllFields) {
        e.preventDefault();
        e.stopPropagation();
        // Reset edit mode
        setEditingTaskId(null);
        setEditingTaskField(null);
        // Blur any focused input within TaskItems
        if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
          activeElement.blur();
        }
        // Also trigger reset of showAllFields by dispatching a custom event
        // TaskItems will listen for this event and reset their showAllFields state
        document.dispatchEvent(new CustomEvent('resetTaskItems'));
        return;
      }
      
      // If modal is open and a task is highlighted, just clear the highlighting
      if (selectedTaskIndex !== -1 || highlightedTaskId !== null) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedTaskIndex(-1);
        setHighlightedTaskId(null);
        // Also close search if it's open
        if (modalSearchVisible) {
          setModalSearchVisible(false);
          setModalSearchText("");
          modalTypingBufferRef.current = "";
        }
        return;
      }
      
      // Don't handle Escape if a modal is open - let the modal handle it
      return;
    }
    if (searchVisible) {
      e.preventDefault();
      e.stopPropagation();
      setSearchVisible(false);
      setSearchText("");
      // Don't clear highlightedItem - leave it as is
      typingBufferRef.current = "";
      return;
    }
    setSelectedCategory(-1);
    setSelectedSubcategory(-1);
  }, null);

  // Command+Enter (Ctrl+Enter on PC) to save when in modal
  useKey("Enter", true, async () => {
    // Don't do anything if editing description
    if (editingDesc) return;
    // Only work when a modal is open
    if (modalCategory) {
      // Save category modal
      if (!modalTitle.trim()) {
        setModalError('Title cannot be empty');
        return;
      }
      try {
        await supabase.from('work').update({ title: modalTitle.trim(), description: modalDesc }).eq('id', modalCategory.id);
        setWorkItems(ws => ws.map(w => w.id === modalCategory.id ? { ...w, title: modalTitle.trim(), description: modalDesc } : w));
        setModalCategory(null);
      } catch (error) {
        console.error('Error updating category:', error);
        setModalError('Failed to save changes');
      }
    } else if (modalChild) {
      // Don't handle cmd+Enter for subepic modal - let the Add Work handler or breakdown form handle it
      // This prevents closing the modal with cmd+Enter
        return;
      }
  }, null);

  // Command+Enter to trigger Add Work button when in subepic modal and breakdown form is not visible
  useKey("Enter", true, (e) => {
    // Only handle when subepic modal is open and breakdown form is not visible
    if (!modalChild || showBreakdownOptions) return;
    
    // Don't handle if editing description, title, or any input field
    if (editingDesc) return;
    
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    // Don't handle if a task is currently being edited
    if (editingTaskId !== null) return;
    
    // Trigger Add Work button
    e.preventDefault();
    e.stopPropagation();
    setShowBreakdownOptions(true);
    setBreakdownNewStoryName("");
    setStoryInputValue("");
    setNoStorySelected(false);
    setBreakdownSelectedStoryId(null);
    setBreakdownTaskEstimate(null);
    setBreakdownTaskAssignee(null);
    setBreakdownTaskStatus('TODO');
  }, null);

  // Keyboard handlers for task navigation in subepic modal
  useKey("Enter", false, (e) => {
    // Only handle in modal when not editing text inputs
    if (!modalChild) return;
    
    // Don't handle if a task is currently being edited - let the input handle it
    if (editingTaskId !== null) {
      return;
    }
    
    // Don't handle if editing a task input field (but allow modal title/description inputs)
    const activeElement = document.activeElement;
    const target = e.target;
    
    // Don't handle if the subepic title input is focused
    if (modalChildTitleInputRef.current && (activeElement === modalChildTitleInputRef.current || target === modalChildTitleInputRef.current)) {
      return;
    }
    
    // Don't handle if the subepic description textarea is focused
    if (descriptionTextareaRef.current && (activeElement === descriptionTextareaRef.current || target === descriptionTextareaRef.current)) {
      return;
    }
    
    // Don't handle if in the breakdown form (new task creation form)
    if (breakdownOptionsRef.current && breakdownOptionsRef.current.contains(target)) {
        return;
      }
    
    // Check both active element and event target to catch all cases
    const elementsToCheck = [activeElement, target].filter(Boolean);
    
    for (const element of elementsToCheck) {
      // Check if the element is within a task item
      const isTaskInput = element.closest('[data-task-id]');
      if (isTaskInput) {
        // Don't handle if editing any field within a task (input, textarea, editable-field, or dropdown)
        if (
          element.tagName === 'INPUT' || 
          element.tagName === 'TEXTAREA' || 
          element.contentEditable === 'true' ||
          element.closest('.editable-field') ||
          (element.closest('input[type="text"]') && element.closest('div[style*="position: relative"]')) ||
          element.closest('[role="combobox"]')
        ) {
          return;
        }
      }
      // Don't handle if in a dropdown (even if not in a task item, to be safe)
      if ((element.closest('input[type="text"]') && element.closest('div[style*="position: relative"]')) || element.closest('[role="combobox"]')) {
        return;
      }
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) {
      e.preventDefault();
      e.stopPropagation();
      setModalSearchVisible(false);
      setModalSearchText("");
      return;
    }
    
    // If a task is highlighted, enter editing mode for that task's title
    if (selectedTaskIndex !== -1 && modalChildTasks.length > 0 && selectedTaskIndex < modalChildTasks.length) {
      e.preventDefault();
      e.stopPropagation();
      const task = modalChildTasks[selectedTaskIndex];
      if (task) {
        // Clear highlighting
        setSelectedTaskIndex(-1);
        setHighlightedTaskId(null);
        // Enter editing mode for the task title
        setEditingTaskId(task.id);
        setEditingTaskField('title');
        // Focus the title input after a brief delay to ensure it's rendered
        setTimeout(() => {
          const taskElement = document.querySelector(`[data-task-id="${task.id}"]`);
          if (taskElement) {
            const titleInput = taskElement.querySelector('input[type="text"]');
            if (titleInput) {
              titleInput.focus();
              titleInput.select();
            }
          }
        }, 0);
      }
      return;
    }
    
    // If no task is selected, highlight the first task
    if (selectedTaskIndex === -1 && modalChildTasks.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(0);
    }
  }, null);

  useKey("ArrowUp", false, (e) => {
    // Only handle in modal when not editing text inputs
    if (!modalChild) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    if (selectedTaskIndex > 0) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(selectedTaskIndex - 1);
    } else if (selectedTaskIndex === 0 && modalChildTasks.length > 0) {
      // Wrap to last task
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(modalChildTasks.length - 1);
    }
  }, null);

  useKey("ArrowDown", false, (e) => {
    // Only handle in modal when not editing text inputs
    if (!modalChild) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    if (selectedTaskIndex < modalChildTasks.length - 1) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(selectedTaskIndex + 1);
    } else if (selectedTaskIndex === modalChildTasks.length - 1 && modalChildTasks.length > 0) {
      // Wrap to first task
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(0);
    } else if (selectedTaskIndex === -1 && modalChildTasks.length > 0) {
      // If nothing selected, select first task
      e.preventDefault();
      e.stopPropagation();
      setSelectedTaskIndex(0);
    }
  }, null);

  // Option/Alt + ArrowUp to move task up
  useKey("ArrowUp", "alt", (e) => {
    // Only handle in modal when a task is highlighted
    if (!modalChild || selectedTaskIndex === -1) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    // Don't handle if a task is currently being edited
    if (editingTaskId !== null) return;
    
    e.preventDefault();
    e.stopPropagation();
    handleMoveTask('up');
  }, null);

  // Option/Alt + ArrowDown to move task down
  useKey("ArrowDown", "alt", (e) => {
    // Only handle in modal when a task is highlighted
    if (!modalChild || selectedTaskIndex === -1) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    // Don't handle if a task is currently being edited
    if (editingTaskId !== null) return;
    
    e.preventDefault();
    e.stopPropagation();
    handleMoveTask('down');
  }, null);

  // Backspace or Delete to delete highlighted task
  useKey("Backspace", false, (e) => {
    // Only handle in modal when a task is highlighted
    if (!modalChild || selectedTaskIndex === -1) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    // Don't handle if a task is currently being edited
    if (editingTaskId !== null) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const task = modalChildTasks[selectedTaskIndex];
    if (task) {
      handleDeleteTask(task.id);
    }
  }, null);

  useKey("Delete", false, (e) => {
    // Only handle in modal when a task is highlighted
    if (!modalChild || selectedTaskIndex === -1) return;
    
    // Don't handle if editing description, title, or any input field
    const activeElement = document.activeElement;
    const isInputOrTextarea = activeElement && (
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.contentEditable === 'true'
    );
    const isDropdownInput = activeElement && (
      activeElement.closest('input[type="text"]') && activeElement.closest('div[style*="position: relative"]')
    );
    if (isInputOrTextarea || isDropdownInput) {
      return;
    }
    
    // Don't handle if search is visible
    if (modalSearchVisible) return;
    
    // Don't handle if a task is currently being edited
    if (editingTaskId !== null) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const task = modalChildTasks[selectedTaskIndex];
    if (task) {
      handleDeleteTask(task.id);
    }
  }, null);

  const handleDelete = async () => {
    if (selectedCategory === -1) return;
    
    // Don't delete if we're currently editing description
    if (editingDesc) return;
    
    // Don't delete if we're currently editing a subcategory
    if (editingChildId) return;
    
    // Don't delete if a modal is open
    if (modalCategory || modalChild) return;
    
    if (selectedSubcategory > -1) {
      // Delete subcategory
      const subcategories = getSubcategories(selectedCategory);
      const subcategory = subcategories[selectedSubcategory];
      
      if (window.confirm(`Are you sure you want to delete the subcategory "${subcategory.title}"?`)) {
        try {
          setFetching(true);
          await supabase.from('work').delete().eq('id', subcategory.id);
          
          // Update local state
          setWorkItems(prev => prev.filter(item => item.id !== subcategory.id));
          
          // Reset selection
          setSelectedCategory(-1);
          setSelectedSubcategory(-1);
          
          // Refresh data
          refresh();
        } catch (error) {
          console.error('Error deleting subcategory:', error);
        } finally {
          setFetching(false);
        }
      }
    } else {
      // Delete category
      const category = categories[selectedCategory];
      const subcategories = getSubcategories(selectedCategory);
      
      if (subcategories.length > 0) {
        // Category has subcategories, require double confirmation
        if (window.confirm(`Are you sure you want to delete the category "${category.title}"? This will also delete all its subcategories.`)) {
          if (window.confirm(`This will permanently delete "${category.title}" and all its ${subcategories.length} subcategories. Are you absolutely sure?`)) {
            try {
              setFetching(true);
              
              // Delete all subcategories first
              for (const subcategory of subcategories) {
                await supabase.from('work').delete().eq('id', subcategory.id);
              }
              
              // Delete the category itself
              await supabase.from('work').delete().eq('id', category.id);
              
              // Update local state
              const idsToDelete = [category.id, ...subcategories.map(s => s.id)];
              setWorkItems(prev => prev.filter(item => !idsToDelete.includes(item.id)));
              
              // Reset selection
              setSelectedCategory(-1);
              setSelectedSubcategory(-1);
              
              // Refresh data
              refresh();
            } catch (error) {
              console.error('Error deleting category with subcategories:', error);
            } finally {
              setFetching(false);
            }
          }
        }
      } else {
        // Category has no subcategories, single confirmation
        if (window.confirm(`Are you sure you want to delete the category "${category.title}"?`)) {
          try {
            setFetching(true);
            await supabase.from('work').delete().eq('id', category.id);
            
            // Update local state
            setWorkItems(prev => prev.filter(item => item.id !== category.id));
            
            // Reset selection
            setSelectedCategory(-1);
            setSelectedSubcategory(-1);
            
            // Refresh data
            refresh();
          } catch (error) {
            console.error('Error deleting category:', error);
          } finally {
            setFetching(false);
          }
        }
      }
    }
  };

  useKey("Delete", false, () => {
    if (editingDesc) return;
    handleDelete();
  }, null);
  useKey("Backspace", false, () => {
    // Don't delete if editing description
    if (editingDesc) return;
    // Don't delete if search is visible
    if (searchVisible) return;
    handleDelete();
  }, null);
  
  // Search functionality - detect when user starts typing
  const typingBufferRef = useRef("");
  const typingTimeoutRef = useRef();

  // Handle any key press for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger search if we're in editing modes or if search is already visible
      if (editingCategoryId || editingChildId || modalCategory || modalChild || searchVisible || editingDesc) return;
      
      // Don't trigger if we're typing in an input field, textarea, or contentEditable element
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') return;
      
      // Don't trigger on special keys
      const specialKeys = ['Escape', 'Tab', 'Enter', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Shift', 'Control', 'Alt', 'Meta'];
      if (specialKeys.includes(e.key)) return;
      
      // Don't trigger on modifier keys
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      
      // Only trigger on letter keys (a-z, A-Z, and accented characters like å, ä, ö, etc.)
      // This regex matches any letter character including Swedish characters (å, ä, ö)
      const isLetter = /^[a-zA-ZåäöÅÄÖ]$/.test(e.key);
      if (!isLetter) return;
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Add character to buffer
      const newBuffer = typingBufferRef.current + e.key;
      typingBufferRef.current = newBuffer;
      
      // Set timeout to clear buffer
      typingTimeoutRef.current = setTimeout(() => {
        typingBufferRef.current = "";
      }, 1000);
      
      // If buffer has at least 1 character, show search
      if (newBuffer.length >= 1) {
        setSearchVisible(true);
        setSearchText(newBuffer);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [editingCategoryId, editingChildId, modalCategory, modalChild, searchVisible, editingDesc]);

  // Search functionality for modal - detect when user starts typing in modal
  const modalTypingBufferRef = useRef("");
  const modalTypingTimeoutRef = useRef();

  // Handle any key press for search in modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle in modal
      if (!modalChild) return;
      
      // Don't trigger search if we're editing task fields
      if (editingTaskId !== null) return;
      
      // If search is visible, check if the Search input is focused
      // If it is, let it handle the keystroke naturally
      if (modalSearchVisible) {
        const searchInput = document.querySelector('input[type="text"][placeholder*="Search tasks"]');
        if (searchInput && (document.activeElement === searchInput || e.target === searchInput)) {
          // Search input is focused, let it handle the keystroke
          return;
        }
        // Search is visible but input not focused yet - continue to capture keystrokes
      }
      
      // Don't trigger if we're typing in an input field, textarea, or contentEditable element
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.contentEditable === 'true') return;
      
      // Don't trigger if we're in a dropdown
      if (e.target.closest('input[type="text"]') && e.target.closest('div[style*="position: relative"]')) return;
      
      // Don't trigger on special keys
      const specialKeys = ['Escape', 'Tab', 'Enter', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Shift', 'Control', 'Alt', 'Meta'];
      if (specialKeys.includes(e.key)) return;
      
      // Don't trigger on modifier keys
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      
      // Only trigger on letter keys (a-z, A-Z, and accented characters like å, ä, ö, etc.)
      const isLetter = /^[a-zA-ZåäöÅÄÖ]$/.test(e.key);
      if (!isLetter) return;
      
      // Clear existing timeout
      if (modalTypingTimeoutRef.current) {
        clearTimeout(modalTypingTimeoutRef.current);
      }
      
      // Add character to buffer
      const newBuffer = modalTypingBufferRef.current + e.key;
      modalTypingBufferRef.current = newBuffer;
      
      // Set timeout to clear buffer
      modalTypingTimeoutRef.current = setTimeout(() => {
        modalTypingBufferRef.current = "";
      }, 1000);
      
      // If buffer has at least 1 character, show search
      if (newBuffer.length >= 1) {
        e.preventDefault();
        e.stopPropagation();
        setModalSearchVisible(true);
        setModalSearchText(newBuffer);
      }
    };

    if (modalChild) {
      window.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (modalTypingTimeoutRef.current) {
        clearTimeout(modalTypingTimeoutRef.current);
      }
    };
  }, [modalChild, editingTaskId, modalSearchVisible]);

  // Note: Escape handler for description editing and search is handled in the main Escape handler above

  useKey("Enter", true, () => {
    // Don't add if editing description
    if (editingDesc) return;
    
    // Don't add if search is visible
    if (searchVisible) return;
    
    // Don't add if we're currently editing a subcategory
    if (editingChildId) return;
    
    // Don't add if a modal is open
    if (modalCategory || modalChild) return;
    
    // If sub-epic is selected, open edit modal with breakdown active
    if (selectedCategory !== -1 && selectedSubcategory > -1) {
      openEditModal(true);
      return;
    }
    
    if (selectedCategory !== -1) {
      // Add new subcategory when a category is selected
      const category = categories[selectedCategory];
      if (category && user?.proj?.id && supabase) {
        // Create new subcategory
        supabase.from('work').insert({
          title: '',
          type: 'subepic',
          parent_id: category.id,
          project_id: user.proj.id,
          created_by: user.id,
          owner_id: user.id
        }).select().then(({ data }) => {
          if (data && data[0]) {
            setWorkItems(prev => [...prev, data[0]]);
            setEditingChildId(data[0].id);
            setChildDraft('');
            // Select the new subcategory
            const subcategories = getSubcategories(selectedCategory);
            setSelectedSubcategory(subcategories.length); // New subcategory will be at the end
          }
        });
      }
    } else {
      // Create new category when nothing is selected
      if (user?.proj?.id && supabase) {
        supabase.from('work').insert({
          title: '',
          type: 'epic',
          parent_id: null,
          project_id: user.proj.id,
          created_by: user.id,
          owner_id: user.id
        }).select().then(({ data }) => {
          if (data && data[0]) {
            setWorkItems(prev => [...prev, data[0]]);
            setEditingCategoryId(data[0].id);
            setCategoryDraft('');
            setShowSuggestions(true); // Show suggestions like when clicking Add Category button
            // Select the new category
            setSelectedCategory(categories.length); // New category will be at the end
          }
        });
      }
    }
  }, null);




  const handleDescSubmit = async () => {
    if (user?.proj?.id && supabase) {
      setFetching(true);
      const newDesc = descDraft.trim() || null; // Use null for empty
      await supabase.from('projects').update({ description: newDesc }).eq('id', user.proj.id);
      setDesc(newDesc);
      setDescDraft('');
      
      try {
        // Only call OpenAI and show suggestions if newDesc is not null
        if (newDesc) {
          const res = await fetch("/.netlify/functions/gpt", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              purpose: "classify",
              project_desc: newDesc,
            })
          });
          const data = await res.json();
          let typeKey = data.message;
          if (typeof typeKey === 'object' && typeKey !== null && typeKey.content) {
            typeKey = typeKey.content;
          } else if (typeof typeKey !== 'string') {
            typeKey = String(typeKey);
          }
          typeKey = typeKey.trim().toLowerCase();
          const allowedKeys = [
            'video_game',
            'mobile_web_app',
            'saas_platform',
            'digital_content_platform',
            'ai_ml_product'
          ];
          if (!allowedKeys.includes(typeKey)) {
            setSuggested([]);
          } else {
            setSuggested(EPICS_DATA[typeKey]?.epics || []);
            // Save the project type to the DB
            await supabase.from('projects').update({ type: typeKey }).eq('id', user.proj.id);
          }
          // Do NOT setShowSuggestions(true) here!
        } else {
          // If description is null, clear suggestions and don't show them
          setSuggested([]);
          setShowSuggestions(false);
        }
      } catch (e) {
        setSuggested([]);
        setShowSuggestions(false);
      }
      
      // Refresh the user context to get updated project data
      await refresh();
      setFetching(false);
      setEditingDesc(false);
    }
  };

  // Filter suggestions to remove those that already exist as work categories
  const filteredSuggestions = suggested.filter(sugg => {
    const title = typeof sugg === 'object' && sugg !== null && 'title' in sugg ? sugg.title : String(sugg);
    // Normalize case and trim whitespace for both suggestion and work item titles
    return !workItems.some(w =>
      w.type === 'epic' &&
      w.parent_id == null &&
      (w.title || '').trim().toLowerCase() === title.trim().toLowerCase()
    );
  });

  useEffect(() => {
    if (
      lastAddedEpicTitle &&
      workItems.some(
        w =>
          w.type === 'epic' &&
          w.parent_id == null &&
          (w.title || '').trim().toLowerCase() === lastAddedEpicTitle.trim().toLowerCase()
      )
    ) {
      setShowSuggestions(false);
      setLastAddedEpicTitle(null);
    }
  }, [workItems, lastAddedEpicTitle]);

  // Calculate the maximum width needed for categories and subcategories
  // Consider both epic titles (14px font) and subepic titles (12px font)
  const epicWidths = categories.map(item => {
    const title = item.title || '';
    return title.length * 8 + 20; // Base width needed for epic title (14px font)
  });
  
  // Calculate widths needed for subepics
  // Subepics are displayed with width = maxCategoryWidth - 12
  // So we need to ensure maxCategoryWidth is at least (subepicWidth + 12)
  const subepicRequiredEpicWidths = workItems
    .filter(w => w.type === 'subepic' && w.parent_id != null)
    .map(child => {
      const title = child.title || '';
      // Subepics use 12px font (~7px per character), need padding
      // Since subepics are displayed as maxCategoryWidth - 12, we need:
      // maxCategoryWidth >= (subepicWidth + 12)
      const subepicWidth = title.length * 7 + 20; // Width needed for subepic at 12px font
      return subepicWidth + 12; // Epic width needed to accommodate this subepic
    });
  
  const maxCategoryWidth = Math.min(
    200, // Maximum width
    Math.max(
      110, // Minimum width
      ...epicWidths,
      ...subepicRequiredEpicWidths
    )
  );

  // Handler function to update a task field
  const handleUpdateTaskField = useCallback(async (taskId, field, value) => {
    if (!supabase || !taskId) return;
    
    const updateData = {};
    if (field === 'title') {
      updateData.title = value;
    } else if (field === 'estimate') {
      updateData.estimate = value === 'remove' ? null : value;
    } else if (field === 'assignee') {
      updateData.owner_id = value === 'remove' ? null : value;
    } else if (field === 'status') {
      updateData.status = value;
    } else if (field === 'story') {
      updateData.story_id = value === 'remove' || value === null ? null : value;
    }
    
    // Handle story assignment - update story's child_order
    if (field === 'story') {
      // Get the current task to check its existing story_id
      const { data: currentTask } = await supabase
        .from("work")
        .select("story_id")
        .eq("id", taskId)
        .single();
      
      const oldStoryId = currentTask?.story_id;
      const newStoryId = value === 'remove' || value === null ? null : value;
      
      // Remove task from old story's child_order if it exists
      if (oldStoryId) {
        const { data: oldStory } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", oldStoryId)
          .single();
        
        if (oldStory) {
          const currentChildOrder = oldStory.child_order || [];
          const newChildOrder = currentChildOrder.filter(id => id !== taskId);
          await supabase
            .from("work")
            .update({ child_order: newChildOrder.length > 0 ? newChildOrder : null })
            .eq("id", oldStoryId);
        }
      }
      
      // Add task to new story's child_order if a new story is assigned
      if (newStoryId) {
        const { data: newStory } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", newStoryId)
          .single();
        
        if (newStory) {
          const currentChildOrder = newStory.child_order || [];
          // Only add if not already in the array
          if (!currentChildOrder.includes(taskId)) {
            const newChildOrder = [...currentChildOrder, taskId];
            await supabase
              .from("work")
              .update({ child_order: newChildOrder })
              .eq("id", newStoryId);
          }
        }
      }
    }
    
    await supabase
      .from("work")
      .update(updateData)
      .eq("id", taskId);
    
    // Update local state
    setModalChildTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, ...updateData } : task
    ));
    
    // Update workItems to keep it in sync (needed for hasActiveTasks indicator)
    setWorkItems(prev => prev.map(item => 
      item.id === taskId ? { ...item, ...updateData } : item
    ));
    
    setEditingTaskId(null);
    setEditingTaskField(null);
  }, [supabase, setWorkItems]);

  // Handler function to create a new story and assign it to a task
  const handleCreateStoryForTask = useCallback(async (taskId, storyTitle) => {
    if (!supabase || !user?.proj?.id || !taskId || !storyTitle?.trim()) return null;
    
    // Get or create unplanned goal
    let { data: unplannedGoal } = await supabase
      .from("goals")
      .select("id, workitems_order")
      .eq("project_id", user.proj.id)
      .eq("type", "unplanned")
      .maybeSingle();
    
    if (!unplannedGoal) {
      const { data: newGoal } = await supabase
        .from("goals")
        .insert({ project_id: user.proj.id, type: "unplanned" })
        .select()
        .single();
      unplannedGoal = newGoal;
    }
    
    if (!unplannedGoal) return null;
    
    // Create new standalone story
    const { data: newStory } = await supabase
      .from("work")
      .insert({
        title: storyTitle.trim(),
        description: modalChild?.description || null,
        goal_id: unplannedGoal.id,
        project_id: user.proj.id,
        parent_id: null,
        type: 'story',
        status: null,
        child_order: [],
        created_by: user.id,
        owner_id: user.id
      })
      .select()
      .single();
    
    if (newStory) {
      // Update goal's workitems_order
      const currentOrder = unplannedGoal.workitems_order || [];
      const newOrder = [...currentOrder, newStory.id];
      await supabase
        .from("goals")
        .update({ workitems_order: newOrder })
        .eq("id", unplannedGoal.id);
      
      // Assign the new story to the task
      await handleUpdateTaskField(taskId, 'story', newStory.id);
      
      // Refresh stories list
      const { data: refreshedStories } = await supabase
        .from("work")
        .select("id, title, goal_id, child_order, status")
        .eq("project_id", user.proj.id)
        .eq("type", "story")
        .is("parent_id", null)
        .order("title", { ascending: true });
      setAllStories(refreshedStories || []);
      
      return newStory;
    }
    
    return null;
  }, [supabase, user, modalChild, handleUpdateTaskField, setAllStories]);

  // Handler function to delete a task
  const handleDeleteTask = useCallback(async (taskId) => {
    if (!supabase || !modalChild || !taskId) return;
    
    const task = modalChildTasks.find(t => t.id === taskId);
    if (!task) return;
    
    if (window.confirm(`Are you sure you want to delete the task "${task.title || 'Untitled Task'}"?`)) {
      try {
        // Delete from database
        await supabase
          .from("work")
          .delete()
          .eq("id", taskId);
        
        // Remove from local state
        const updatedTasks = modalChildTasks.filter(t => t.id !== taskId);
        setModalChildTasks(updatedTasks);
        
        // Update child_order in subepic to remove the deleted task
        const newChildOrder = (modalChild.child_order || []).filter(id => id !== taskId);
        await supabase
          .from("work")
          .update({ child_order: newChildOrder })
          .eq("id", modalChild.id);
        
        // Update modalChild's child_order in local state
        setModalChild(prev => prev ? { ...prev, child_order: newChildOrder } : null);
        
        // Update workItems to reflect the deletion
        setWorkItems(ws => ws.filter(w => w.id !== taskId));
        
        // Reset selection
        setSelectedTaskIndex(-1);
        setHighlightedTaskId(null);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  }, [supabase, modalChild, modalChildTasks, setWorkItems]);

  // Handler function to track when drag starts
  const handleSubEpicDragStart = useCallback(() => {
    // Set flag immediately when drag starts to prevent onClick
    justDraggedSubEpicRef.current = true;
  }, []);
  
  // Handler function to handle drag end for subepic reordering and moving between epics
  const handleSubEpicDragEnd = useCallback(async (result) => {
    // Keep flag true for longer to ensure any pending onClick events are blocked
    // Use requestAnimationFrame to ensure this runs after any pending onClick
    requestAnimationFrame(() => {
      setTimeout(() => {
        justDraggedSubEpicRef.current = false;
      }, 500);
    });
    
    if (!result.destination || !supabase) {
      // If drag was cancelled, still keep flag for a bit to prevent accidental clicks
      requestAnimationFrame(() => {
        setTimeout(() => {
          justDraggedSubEpicRef.current = false;
        }, 200);
      });
      return;
    }
    
    const { source, destination, draggableId } = result;
    const subepicId = parseInt(draggableId.replace('subepic-', ''));
    const sourceEpicId = parseInt(source.droppableId.replace('epic-', ''));
    const destEpicId = parseInt(destination.droppableId.replace('epic-', ''));
    
    // Find the subepic being moved
    const subepic = workItems.find(w => w.id === subepicId && w.type === 'subepic');
    if (!subepic) return;
    
    // Get source and destination epics
    const sourceEpic = workItems.find(w => w.id === sourceEpicId && w.type === 'epic');
    const destEpic = workItems.find(w => w.id === destEpicId && w.type === 'epic');
    if (!sourceEpic || !destEpic) return;
    
    // Get current subepics for both epics (using getSubcategories with category indices)
    const sourceCategoryIndex = categories.findIndex(c => c.id === sourceEpicId);
    const destCategoryIndex = categories.findIndex(c => c.id === destEpicId);
    const sourceSubepics = getSubcategories(sourceCategoryIndex);
    const destSubepics = getSubcategories(destCategoryIndex);
    
    // Remove subepic from source epic
    const sourceSubepicsFiltered = sourceSubepics.filter(s => s.id !== subepicId);
    
    // Insert subepic into destination epic at the correct position
    const destSubepicsWithNew = Array.from(destSubepics);
    if (sourceEpicId === destEpicId) {
      // Moving within same epic
      destSubepicsWithNew.splice(source.index, 1);
      destSubepicsWithNew.splice(destination.index, 0, subepic);
    } else {
      // Moving between epics
      destSubepicsWithNew.splice(destination.index, 0, subepic);
    }
    
    // Calculate new child orders
    const sourceChildOrder = sourceSubepicsFiltered.map(s => s.id);
    const destChildOrder = destSubepicsWithNew.map(s => s.id);
    
    // Update workItems optimistically FIRST for instant UI update (no blinking)
    setWorkItems(ws => {
      let updated = ws.map(w => {
        if (sourceEpicId === destEpicId) {
          // Moving within same epic - only update that epic once
          if (w.id === sourceEpicId) {
            return { ...w, child_order: destChildOrder.length > 0 ? destChildOrder : null };
          }
        } else {
          // Moving between epics - update both
          if (w.id === sourceEpicId) {
            return { ...w, child_order: sourceChildOrder.length > 0 ? sourceChildOrder : null };
          }
          if (w.id === destEpicId) {
            return { ...w, child_order: destChildOrder.length > 0 ? destChildOrder : null };
          }
          if (w.id === subepicId) {
            return { ...w, parent_id: destEpicId };
          }
        }
        return w;
      });
      return updated;
    });
    
    // Then persist to database (UI already updated optimistically, so no blinking)
    // Use Promise.all to execute all updates in parallel
    const updatePromises = [];
    
    if (sourceEpicId === destEpicId) {
      // Moving within same epic - only update once
      updatePromises.push(
        supabase
          .from("work")
          .update({ child_order: destChildOrder.length > 0 ? destChildOrder : null })
          .eq("id", sourceEpicId)
      );
    } else {
      // Moving between epics - update both epics
      updatePromises.push(
        supabase
          .from("work")
          .update({ child_order: sourceChildOrder.length > 0 ? sourceChildOrder : null })
          .eq("id", sourceEpicId),
        supabase
          .from("work")
          .update({ child_order: destChildOrder.length > 0 ? destChildOrder : null })
          .eq("id", destEpicId)
      );
      
      // Update subepic's parent_id
      updatePromises.push(
        supabase
          .from("work")
          .update({ parent_id: destEpicId })
          .eq("id", subepicId)
      );
    }
    
    // Execute all updates in parallel and await completion
    // (UI already updated, so awaiting doesn't cause blinking)
    await Promise.all(updatePromises);
  }, [supabase, workItems, categories, getSubcategories, setWorkItems]);
  
  // Handler function to handle drag end for task reordering
  const handleTaskDragEnd = useCallback(async (result) => {
    if (!result.destination || !modalChild || !supabase) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Reorder tasks locally
    const reorderedTasks = Array.from(modalChildTasks);
    const [removed] = reorderedTasks.splice(sourceIndex, 1);
    reorderedTasks.splice(destIndex, 0, removed);
    
    setModalChildTasks(reorderedTasks);
    
    // Update child_order in subepic
    const newChildOrder = reorderedTasks.map(t => t.id);
    await supabase
      .from("work")
      .update({ child_order: newChildOrder })
      .eq("id", modalChild.id);
    
    // Update modalChild's child_order in local state
    setModalChild(prev => prev ? { ...prev, child_order: newChildOrder } : null);
    
    // Update workItems to reflect the new child_order so it persists when modal reopens
    setWorkItems(ws => ws.map(w => w.id === modalChild.id ? { ...w, child_order: newChildOrder } : w));
  }, [modalChildTasks, modalChild, supabase, setWorkItems]);

  // Handler function to move task up or down in the list
  const handleMoveTask = useCallback(async (direction) => {
    if (!modalChild || !supabase || selectedTaskIndex === -1 || modalChildTasks.length < 2) return;
    
    const currentIndex = selectedTaskIndex;
    let newIndex;
    
    if (direction === 'up') {
      if (currentIndex === 0) return; // Already at top
      newIndex = currentIndex - 1;
    } else if (direction === 'down') {
      if (currentIndex === modalChildTasks.length - 1) return; // Already at bottom
      newIndex = currentIndex + 1;
    } else {
      return;
    }
    
    // Reorder tasks locally
    const reorderedTasks = Array.from(modalChildTasks);
    const [removed] = reorderedTasks.splice(currentIndex, 1);
    reorderedTasks.splice(newIndex, 0, removed);
    
    setModalChildTasks(reorderedTasks);
    
    // Update selected index to follow the moved task
    setSelectedTaskIndex(newIndex);
    
    // Update child_order in subepic
    const newChildOrder = reorderedTasks.map(t => t.id);
    await supabase
      .from("work")
      .update({ child_order: newChildOrder })
      .eq("id", modalChild.id);
    
    // Update modalChild's child_order in local state
    setModalChild(prev => prev ? { ...prev, child_order: newChildOrder } : null);
    
    // Update workItems to reflect the new child_order so it persists when modal reopens
    setWorkItems(ws => ws.map(w => w.id === modalChild.id ? { ...w, child_order: newChildOrder } : w));
  }, [modalChildTasks, modalChild, supabase, selectedTaskIndex, setWorkItems]);

  // Handler functions for breakdown in modal
  const handleCreateNewTask = async () => {
    if (!user?.proj?.id || !supabase || !modalChild) return;
    
    let existingStory = null;
    
    // Check if "no story" option is selected
    // Also check if storyInputValue explicitly says "no story" (case insensitive)
    const storyInputIsNoStory = storyInputValue.trim().toLowerCase() === 'no story';
    const isNoStorySelected = noStorySelected || breakdownSelectedStoryId === -1 || storyInputIsNoStory;
    
    if (isNoStorySelected) {
      // Create task without story (story_id = null)
      const taskTitle = breakdownNewStoryName.trim() || "Untitled Task";
      const { data: newTask } = await supabase
        .from("work")
        .insert({
          title: taskTitle,
          description: modalChild.description || null,
          project_id: user.proj.id,
          parent_id: modalChild.id, // Task is directly under sub-epic
          story_id: null, // No story association
          type: 'task',
          status: breakdownTaskStatus || 'TODO', // Set explicit status so task shows up in Work page
          child_order: [],
          created_by: user.id,
          owner_id: breakdownTaskAssignee || user.id,
          estimate: breakdownTaskEstimate || null
        })
        .select()
        .single();
      
      if (newTask) {
        // Update subepic's child_order to include the new task
        const { data: subepicData } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", modalChild.id)
          .single();
        
        if (subepicData) {
          const currentChildOrder = subepicData.child_order || [];
          const newChildOrder = [...currentChildOrder, newTask.id];
          await supabase
            .from("work")
            .update({ child_order: newChildOrder })
            .eq("id", modalChild.id);
          
          // Update modalChild's child_order in local state
          setModalChild(prev => prev ? { ...prev, child_order: newChildOrder } : null);
        }
        
        // Refresh work items
        const { data } = await supabase
          .from("work")
          .select()
          .eq("project_id", user.proj.id)
          .order("id", { ascending: true });
        setWorkItems(data || []);
        
        // Reset form fields
        setBreakdownNewStoryName("");
        setBreakdownTaskEstimate(null);
        setBreakdownTaskAssignee(null);
        setBreakdownTaskStatus('TODO');
        setNoStorySelected(false);
        setBreakdownSelectedStoryId(null);
        setStoryInputValue("");
        
        // Refresh children tasks directly under this sub-epic
        const { data: childTasks } = await supabase
          .from("work")
          .select("id, title, story_id, owner_id, estimate, status, child_order")
          .eq("project_id", user.proj.id)
          .eq("parent_id", modalChild.id)
          .eq("type", "task")
          .order("title", { ascending: true });
        
        // Fetch all standalone stories to get story titles
        const { data: allStories } = await supabase
          .from("work")
          .select("id, title")
          .eq("project_id", user.proj.id)
          .eq("type", "story")
          .is("parent_id", null)
          .order("title", { ascending: true });
        
        // Add story information to each task
        let tasksWithStories = (childTasks || []).map(task => {
          const story = allStories?.find(s => s.id === task.story_id);
          return {
            ...task,
            storyTitle: story?.title || null
          };
        });
        
        // Order tasks by child_order if it exists on the parent subepic
        // Refresh modalChild to get updated child_order
        const { data: updatedSubepic } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", modalChild.id)
          .single();
        
        if (updatedSubepic?.child_order && Array.isArray(updatedSubepic.child_order) && updatedSubepic.child_order.length > 0) {
          const orderedTasks = [];
          const unorderedTasks = [];
          
          updatedSubepic.child_order.forEach(taskId => {
            const task = tasksWithStories.find(t => t.id === taskId);
            if (task) orderedTasks.push(task);
          });
          
          tasksWithStories.forEach(task => {
            if (!updatedSubepic.child_order.includes(task.id)) {
              unorderedTasks.push(task);
            }
          });
          
          tasksWithStories = [...orderedTasks, ...unorderedTasks];
          
          // Update modalChild's child_order in local state
          setModalChild(prev => prev ? { ...prev, child_order: updatedSubepic.child_order } : null);
        }
        
        setModalChildTasks(tasksWithStories);
        
        // Refresh breakdown stories list
        const { data: refreshedBreakdownStories } = await supabase
          .from("work")
          .select("id, title, goal_id, child_order, status")
          .eq("project_id", user.proj.id)
          .eq("type", "story")
          .is("parent_id", null)
          .order("title", { ascending: true });
        const nonCompletedStories = (refreshedBreakdownStories || []).filter(story => story.status !== 'COMPLETE');
        setBreakdownStories(nonCompletedStories);
        
        // Focus is handled by BreakdownForm component
        
        // Refresh AuthContext to update Work page
        await refresh();
      }
      return;
    }
    
    // If a story is selected, use it; otherwise create a new one
    // Note: breakdownSelectedStoryId === -1 means "No Story" and should have been handled above
    // Double-check: if "No Story" is selected, we should not reach here, but add defensive check
    // Reuse storyInputIsNoStory variable declared above
    if (noStorySelected || breakdownSelectedStoryId === -1 || storyInputIsNoStory) {
      // "No Story" is selected - don't create or use a story
      existingStory = null;
    } else if (breakdownSelectedStoryId && breakdownSelectedStoryId !== -1) {
      // Use the selected story
      const { data: story } = await supabase
        .from("work")
        .select("id, title, goal_id, child_order")
        .eq("id", breakdownSelectedStoryId)
        .eq("project_id", user.proj.id)
        .single();
      existingStory = story;
    } else {
      // Only create a new story if the user has explicitly typed a story name
      // If storyInputValue is empty, don't create a story - just create the task without a story
      const hasStoryInput = storyInputValue && storyInputValue.trim().length > 0;
      
      if (hasStoryInput) {
        // Create a new standalone story (parent_id = null)
        // Get or create unplanned goal
        let { data: unplannedGoal } = await supabase
          .from("goals")
          .select("id, workitems_order")
          .eq("project_id", user.proj.id)
          .eq("type", "unplanned")
          .maybeSingle();
        
        if (!unplannedGoal) {
          const { data: newGoal } = await supabase
            .from("goals")
            .insert({ project_id: user.proj.id, type: "unplanned" })
            .select()
            .single();
          unplannedGoal = newGoal;
        }
        
        if (unplannedGoal) {
          // Create new standalone story with the name from the story input field (storyInputValue)
          const storyTitle = storyInputValue.trim() || "Untitled Story";
          const { data: newStory } = await supabase
            .from("work")
            .insert({
              title: storyTitle,
              description: modalChild.description || null,
              goal_id: unplannedGoal.id,
              project_id: user.proj.id,
              parent_id: null, // Standalone story, not under sub-epic
              type: 'story',
              status: null,
              child_order: [],
              created_by: user.id,
              owner_id: user.id
            })
            .select()
            .single();
          
          if (newStory) {
            // Update goal's workitems_order
            const currentOrder = unplannedGoal.workitems_order || [];
            const newOrder = [...currentOrder, newStory.id];
            await supabase
              .from("goals")
              .update({ workitems_order: newOrder })
              .eq("id", unplannedGoal.id);
            
            existingStory = newStory;
          }
        }
      } else {
        // No story input provided - don't create a story, just leave existingStory as null
        existingStory = null;
      }
    }
    
    // Create the task with parent_id = sub-epic and story_id = story (or null if no story)
    const taskTitle = breakdownNewStoryName.trim() || "Untitled Task";
    const { data: newTask } = await supabase
      .from("work")
      .insert({
        title: taskTitle,
        description: modalChild.description || null,
        project_id: user.proj.id,
        parent_id: modalChild.id, // Task is directly under sub-epic
        story_id: existingStory?.id || null, // Task references the story, or null if no story
        type: 'task',
        status: breakdownTaskStatus || 'TODO', // Set explicit status so task shows up in Work page
        child_order: [],
        created_by: user.id,
        owner_id: breakdownTaskAssignee || user.id,
        estimate: breakdownTaskEstimate || null
      })
      .select()
      .single();
    
    if (newTask) {
      // Update subepic's child_order to include the new task
      const { data: subepicData } = await supabase
        .from("work")
        .select("child_order")
        .eq("id", modalChild.id)
        .single();
      
      if (subepicData) {
        const currentChildOrder = subepicData.child_order || [];
        const newChildOrder = [...currentChildOrder, newTask.id];
        await supabase
          .from("work")
          .update({ child_order: newChildOrder })
          .eq("id", modalChild.id);
        
        // Update modalChild's child_order in local state
        setModalChild(prev => prev ? { ...prev, child_order: newChildOrder } : null);
      }
      
      if (existingStory) {
        // Update story's child_order to include the new task (only if story exists)
        const { data: storyData } = await supabase
          .from("work")
          .select("child_order")
          .eq("id", existingStory.id)
          .single();
        
        if (storyData) {
          const currentChildOrder = storyData.child_order || [];
          const newChildOrder = [...currentChildOrder, newTask.id];
          await supabase
            .from("work")
            .update({ child_order: newChildOrder })
            .eq("id", existingStory.id);
        }
      }
      
      // Refresh work items
      const { data } = await supabase
        .from("work")
        .select()
        .eq("project_id", user.proj.id)
        .order("id", { ascending: true });
      setWorkItems(data || []);
      
      // Reset form fields
      setBreakdownNewStoryName("");
      setBreakdownTaskEstimate(null);
      setBreakdownTaskAssignee(null);
      setBreakdownTaskStatus('TODO');
      setNoStorySelected(false);
      setBreakdownSelectedStoryId(null);
      setStoryInputValue("");
      // Focus is handled by BreakdownForm component
      
      // Refresh children tasks directly under this sub-epic
      const { data: childTasks } = await supabase
        .from("work")
        .select("id, title, story_id, owner_id, estimate, status, child_order")
        .eq("project_id", user.proj.id)
        .eq("parent_id", modalChild.id)
        .eq("type", "task")
        .order("title", { ascending: true });
      
      // Fetch all standalone stories to get story titles
      const { data: allStories } = await supabase
        .from("work")
        .select("id, title")
        .eq("project_id", user.proj.id)
        .eq("type", "story")
        .is("parent_id", null)
        .order("title", { ascending: true });
      
      // Add story information to each task
      let tasksWithStories = (childTasks || []).map(task => {
        const story = allStories?.find(s => s.id === task.story_id);
        return {
          ...task,
          storyTitle: story?.title || null
        };
      });
      
      // Order tasks by child_order if it exists on the parent subepic
      if (modalChild?.child_order && Array.isArray(modalChild.child_order) && modalChild.child_order.length > 0) {
        const orderedTasks = [];
        const unorderedTasks = [];
        
        modalChild.child_order.forEach(taskId => {
          const task = tasksWithStories.find(t => t.id === taskId);
          if (task) orderedTasks.push(task);
        });
        
        tasksWithStories.forEach(task => {
          if (!modalChild.child_order.includes(task.id)) {
            unorderedTasks.push(task);
          }
        });
        
        tasksWithStories = [...orderedTasks, ...unorderedTasks];
      }
      
      setModalChildTasks(tasksWithStories);
      
      // Refresh breakdown stories list
      const { data: refreshedBreakdownStories } = await supabase
        .from("work")
        .select("id, title, goal_id, child_order, status")
        .eq("project_id", user.proj.id)
        .eq("type", "story")
        .is("parent_id", null)
        .order("title", { ascending: true });
      // Filter out completed stories
      const nonCompletedStories = (refreshedBreakdownStories || []).filter(story => story.status !== 'COMPLETE');
      setBreakdownStories(nonCompletedStories);
      
      // Save the story ID to settings as the most recent story
      if (existingStory && user?.proj?.id && user?.id) {
        const { data: existingSetting } = await supabase
          .from("settings")
          .select("id")
          .eq("name", "recentTaskStory")
          .eq("project_id", user.proj.id)
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (existingSetting) {
          await supabase
            .from("settings")
            .update({ value: existingStory.id.toString() })
            .eq("id", existingSetting.id);
        } else {
          await supabase
            .from("settings")
            .insert({
              name: "recentTaskStory",
              value: existingStory.id.toString(),
              project_id: user.proj.id,
              user_id: user.id,
            });
        }
      }
      
      // Refresh AuthContext to update Work page
      await refresh();
    }
    
    setShowBreakdownOptions(false);
    setBreakdownSelectedStory(null);
    setBreakdownNewStoryName("");
    setStoryInputValue("");
    setNoStorySelected(false);
    setBreakdownSelectedStoryId(null);
    setShowStoryDropdown(false);
  };
  
  // Keep ref in sync with function
  useEffect(() => {
    handleCreateNewTaskRef.current = handleCreateNewTask;
  });

  return (
    <div 
      onClick={() => {
        // Don't clear selection if search is visible
        if (!searchVisible) {
          setSelectedCategory(-1);
          setSelectedSubcategory(-1);
        }
      }}
      style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 32,
      paddingTop: 0,
      minHeight: '100vh',
      boxSizing: 'border-box',
    }}>
      <Search
        isVisible={searchVisible}
        onClose={() => {
          setSearchVisible(false);
          setSearchText("");
          // Don't clear highlightedItem, selectedCategory, or selectedSubcategory - leave them as is
          typingBufferRef.current = "";
        }}
        onSearch={handleSearch}
        onTextChange={setSearchText}
        placeholder="Search categories and subcategories..."
        initialText={searchText}
        position="top"
        onEnter={() => {
          // Just close Search and leave the sub-epic highlighted
          setSearchVisible(false);
          setSearchText("");
          typingBufferRef.current = "";
        }}
        onEscape={() => {
          // Just close Search without clearing highlight - leave highlighted item as is
          setSearchVisible(false);
          setSearchText("");
          typingBufferRef.current = "";
        }}
      />
      
      {/* Title, Description, and 3D Component Container */}
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {(() => {
          const hasEpics = workItems.filter(w => w.type === 'epic' && w.parent_id == null).length > 0;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, maxWidth: '50%', justifyContent: 'center', flexWrap: 'wrap', marginLeft: hasEpics ? -100 : 0 }}>
          {/* 3D Component on the left */}
          {user?.proj?.description && hasEpics ? (
            <div
              style={{ transition: 'transform 0.2s cubic-bezier(.4,2,.6,1)', position: 'relative', zIndex: 0, flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <CategoryCubes
                categories={workItems.filter(w => w.type === 'epic' && w.parent_id == null)}
                highlightedCategoryId={highlightedCategoryId || (selectedCategory !== -1 ? workItems.filter(w => w.type === 'epic' && w.parent_id == null)[selectedCategory]?.id : null)}
                onCubeClick={handleCubeClick}
              />
            </div>
          ) : null}
          
          {/* Title and Description on the right */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0, marginLeft: hasEpics ? -70 : 0 }}>
          <h2 style={{ marginTop: hasEpics ? 0 : 40 }}>{user?.proj?.name || 'Project'}</h2>
          
          {/* Description input or static text */}
          {!user?.proj?.description && !editingDesc && (
            <div style={{ width: 400, maxWidth: '90%', marginTop: 32, zIndex: 10, marginBottom: 30, position: 'relative' }}>
              <Input
                value={descDraft}
                onChange={e => setDescDraft(e.target.value)}
                placeholder="What type of project is it?"
                style={{ textAlign: 'center', fontSize: 17, fontFamily: 'inherit', background: fetching ? '#f5f5f5' : '#fff', color: fetching ? '#999' : '#111', border: '1px solid #bbb', borderRadius: 7, marginBottom: 8, opacity: fetching ? 0.7 : 1, cursor: fetching ? 'wait' : 'text' }}
                className="project-desc-input"
                onKeyDown={e => {
                  if (e.key === 'Enter' && !fetching) handleDescSubmit();
                }}
                disabled={fetching}
              />
              <div style={{ textAlign: 'right', fontSize: 11, color: fetching ? '#bbb' : '#999', marginTop: -4, marginBottom: 4 }}>
                {fetching ? 'Submitting...' : `${descDraft.length}/20 minimum`}
              </div>
              {descDraft.length >= 20 ? (
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                  <button
                    style={{ padding: '6px 18px', borderRadius: 5, border: 'none', background: fetching ? '#95a5a6' : '#27ae60', color: 'white', fontWeight: 'bold', cursor: fetching ? 'wait' : 'pointer', opacity: fetching ? 0.8 : 1 }}
                    onClick={handleDescSubmit}
                    disabled={fetching}
                  >
                    {fetching ? (
                      <>
                        <span className="spinner" style={{ display: 'inline-block', width: 18, height: 18, border: '3px solid #fff', borderTop: '3px solid #27ae60', borderRadius: '50%', animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 8 }} />
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', marginTop: 12, color: fetching ? '#bbb' : '#666', fontSize: 14, opacity: fetching ? 0.5 : 1 }}>
                  <div style={{ marginBottom: 8 }}>Examples:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                    <div style={{ cursor: fetching ? 'wait' : 'pointer', padding: '4px 8px', borderRadius: 4, background: '#f8f9fa', border: '1px solid #e9ecef', pointerEvents: fetching ? 'none' : 'auto' }} onClick={() => !fetching && setDescDraft('Mobile app for task management')}>Mobile app for task management</div>
                    <div style={{ cursor: fetching ? 'wait' : 'pointer', padding: '4px 8px', borderRadius: 4, background: '#f8f9fa', border: '1px solid #e9ecef', pointerEvents: fetching ? 'none' : 'auto' }} onClick={() => !fetching && setDescDraft('Rougelite video game for PC and Console')}>Rougelite video game for PC and Console</div>
                    <div style={{ cursor: fetching ? 'wait' : 'pointer', padding: '4px 8px', borderRadius: 4, background: '#f8f9fa', border: '1px solid #e9ecef', pointerEvents: fetching ? 'none' : 'auto' }} onClick={() => !fetching && setDescDraft('SaaS platform for team collaboration')}>SaaS platform for team collaboration</div>
                  </div>
                </div>
              )}
            </div>
          )}
          {user?.proj?.description && (
            <div
              style={{
                width: 220,
                textAlign: 'center',
                color: '#6c757d',
                lineHeight: 'normal',
                cursor: editingDesc ? 'text' : 'pointer',
                padding: 4,
                position: 'relative',
                zIndex: 10,
                background: 'transparent',
                marginTop: -2,
                marginBottom: editingDesc ? 8 : 8,
                fontSize: 14,
              }}
              title={editingDesc ? undefined : "Click to edit description"}
              onClick={() => {
                if (!editingDesc) {
                  setEditingDesc(true);
                  setDescDraft(user.proj.description);
                }
              }}
              onMouseEnter={e => {
                if (!editingDesc) {
                  const icon = e.currentTarget.querySelector('.edit-pen-icon');
                  if (icon) icon.style.opacity = 1;
                }
              }}
              onMouseLeave={e => {
                if (!editingDesc) {
                  const icon = e.currentTarget.querySelector('.edit-pen-icon');
                  if (icon) icon.style.opacity = 0;
                }
              }}
            >
              {editingDesc ? (
                <>
                  <textarea
                    ref={descTextareaRef}
                    value={descDraft}
                    onChange={e => {
                      setDescDraft(e.target.value);
                      // Auto-resize on change
                      const textarea = e.target;
                      textarea.style.height = 'auto';
                      textarea.style.height = textarea.scrollHeight + 'px';
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDescSubmit();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingDesc(false);
                        setDescDraft(user.proj.description);
                      }
                    }}
                    disabled={fetching}
                    autoFocus
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      color: '#6c757d',
                      fontSize: 14,
                      textAlign: 'center',
                      fontFamily: 'inherit',
                      padding: 0,
                      margin: 0,
                      wordBreak: 'break-word',
                      resize: 'none',
                      overflow: 'hidden',
                      minHeight: '1em',
                      lineHeight: '1.4',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }}>
                    <button
                      style={{ padding: '4px 12px', borderRadius: 4, border: 'none', background: '#27ae60', color: 'white', fontWeight: 'bold', fontSize: 12, cursor: fetching ? 'wait' : 'pointer', opacity: fetching ? 0.7 : 1 }}
                      onClick={handleDescSubmit}
                      disabled={fetching}
                    >
                      {fetching ? (
                        <span className="spinner" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff', borderTop: '2px solid #27ae60', borderRadius: '50%', animation: 'spin 1s linear infinite', verticalAlign: 'middle' }} />
                      ) : (
                        'Save'
                      )}
                    </button>
                    <button
                      style={{ padding: '4px 12px', borderRadius: 4, border: 'none', background: '#bbb', color: 'white', fontWeight: 'bold', fontSize: 12, cursor: fetching ? 'not-allowed' : 'pointer', opacity: fetching ? 0.7 : 1 }}
                      onClick={() => {
                        setEditingDesc(false);
                        setDescDraft(user.proj.description);
                      }}
                      disabled={fetching}
                    >Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ display: 'inline-block', wordBreak: 'break-word' }}>{user.proj.description}</span>
                  <span
                    className="edit-pen-icon"
                    style={{
                      position: 'absolute',
                      right: -12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none',
                      color: '#6c757d',
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 18
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.7 3.29a1 1 0 0 1 1.42 0l.59.59a1 1 0 0 1 0 1.42l-9.3 9.3-2.12.7.7-2.12 9.3-9.3zM3 17h14v2H3v-2z" fill="currentColor"/>
                    </svg>
                  </span>
                </>
              )}
            </div>
          )}
          </div>
            </div>
          );
        })()}
      </div>
      {/* Only show category UI if project has description */}
      {user?.proj?.description && (
          <div style={{ marginBottom: 20, position: 'relative', width: '100%' }}>
          {workItems.filter(w => w.type === 'epic' && w.parent_id == null).length === 0 && (
              <div style={{
                color: '#fff',
                fontWeight: 300,
                fontSize: 15,
                width: 320,
                textAlign: 'center',
                margin: '40px auto 18px auto',
                opacity: 0.85,
                letterSpacing: 0.1,
              }}>
                {`Let's break it down into the largest categories of the work that needs to be done`}
              </div>
            )}
          {workItems.filter(w => w.type === 'epic' && w.parent_id == null).length === 0 && (
              <button
                style={{
                  background: 'rgba(187, 187, 187, 1)',
                  color: '#111',
                  borderRadius: 7,
                  border: 'none',
                padding: '10px 16px',
                  fontWeight: 'bold',
                  fontSize: 14,
                  boxShadow: '0 1px 4px #0001',
                  textAlign: 'center',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  margin: '0 auto',
                  transition: 'background 0.15s, color 0.15s',
                  whiteSpace: 'nowrap',
                }}
              onClick={async () => {
                if (!user?.proj?.id || !supabase) return;
                const { data } = await supabase.from('work').insert({
                  title: '',
                  type: 'epic',
                  parent_id: null,
                  project_id: user.proj.id,
                  created_by: user.id,
                  owner_id: user.id
                }).select();
                if (data && data[0]) {
                  setWorkItems(prev => [...prev, data[0]]);
                  setEditingCategoryId(data[0].id);
                  setCategoryDraft('');
                  setShowSuggestions(true);
                }
              }}
              >
                Add Category
              </button>
            )}
          {/* Render work categories as cards, with in-place editing for the new/empty one */}
            {workItems.filter(w => w.type === 'epic' && w.parent_id == null).length > 0 && (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <DragDropContext 
                  onDragStart={handleSubEpicDragStart} 
                  onDragEnd={handleSubEpicDragEnd}
                  getContainerForClone={() => document.body}
                >
                <div style={{
                  display: 'flex',
                  gap: 5, // Reduced from 8 to 5 (3px closer)
                  marginTop: 6,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                alignItems: 'flex-start', // <-- changed from 'center' to 'flex-start'
                  width: 'auto',
                  paddingBottom: 16,
                  position: 'relative',
                }}>
                <div style={{ width: 32, height: 1 }} />
                {workItems.filter(w => w.type === 'epic' && w.parent_id == null).map((item, i) => {
                  return (
                    <div 
                      key={item.id} 
                      data-category-id={item.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        minWidth: 70,
                        maxWidth: 260,
                        justifyContent: 'flex-end',
                      }}
                      onMouseEnter={() => {
                        setHighlightedCategoryId(item.id);
                      }}
                      onMouseLeave={() => {
                        setHighlightedCategoryId(null);
                      }}
                    >
                      <div
                        style={{
                          background: 'rgba(187, 187, 187, 1)',
                          color: '#111',
                          borderRadius: 5,
                          padding: 10,
                          fontWeight: 500,
                          fontSize: 14,
                          boxShadow: '0 1px 4px #0001',
                          textAlign: 'center',
                        width: maxCategoryWidth, // Use calculated width
                          pointerEvents: 'auto',
                          position: 'relative',
                          cursor: editingCategoryId === item.id ? 'text' : 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingCategoryId === item.id) return;
                          setSelectedCategory(i);
                          setSelectedSubcategory(-1);
                          setModalCategory(item);
                          setModalTitle(item.title);
                          setModalDesc(item.description || "");
                          setModalError("");
                        }}
                      >
                        {editingCategoryId === item.id ? (
                          <input
                            type="text"
                            value={categoryDraft}
                            autoFocus
                            onChange={e => {
                              if (e.target.value.length <= 16) {
                                setCategoryDraft(e.target.value);
                              }
                            }}
                            onBlur={async () => {
                              const hasChildren = workItems.some(w => w.parent_id === item.id);
                              if (!categoryDraft.trim() && supabase && !hasChildren) {
                                await supabase.from('work').delete().eq('id', item.id);
                                setWorkItems(ws => ws.filter(w => w.id !== item.id));
                              } else if (categoryDraft.trim() && supabase) {
                                await supabase.from('work').update({ title: categoryDraft.trim() }).eq('id', item.id);
                                setWorkItems(ws => ws.map(w => w.id === item.id ? { ...w, title: categoryDraft.trim() } : w));
                              }
                              setEditingCategoryId(null);
                              setCategoryDraft('');
                              if (!hoveredSuggestionPanel) {
                                setTimeout(() => setShowSuggestions(false), 150);
                              }
                            }}
                            onKeyDown={async e => {
                              const hasChildren = workItems.some(w => w.parent_id === item.id);
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!categoryDraft.trim() && supabase && !hasChildren) {
                                  await supabase.from('work').delete().eq('id', item.id);
                                  setWorkItems(ws => ws.filter(w => w.id !== item.id));
                                } else if (categoryDraft.trim() && supabase) {
                                  await supabase.from('work').update({ title: categoryDraft.trim() }).eq('id', item.id);
                                  setWorkItems(ws => ws.map(w => w.id === item.id ? { ...w, title: categoryDraft.trim() } : w));
                                }
                                setEditingCategoryId(null);
                                setCategoryDraft('');
                                setShowSuggestions(false);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!categoryDraft.trim() && supabase && !hasChildren) {
                                  await supabase.from('work').delete().eq('id', item.id);
                                  setWorkItems(ws => ws.filter(w => w.id !== item.id));
                                }
                                setEditingCategoryId(null);
                                setCategoryDraft('');
                                setShowSuggestions(false);
                              }
                            }}
                            style={{
                              width: maxCategoryWidth - 20,
                              border: 'none',
                              fontSize: 14,
                              fontWeight: 'bold',
                              color: '#111',
                              background: 'transparent',
                              textAlign: 'center',
                              outline: 'none',
                              boxShadow: 'none',
                              padding: 0,
                              margin: 0,
                              height: 14,
                              lineHeight: 1,
                              display: 'inline-block',
                              verticalAlign: 'middle',
                            }}
                          />
                        ) : (
                          <div style={{ fontWeight: 'bold', fontSize: 14, color: '#111', userSelect: 'none' }}>{item.title}</div>
                        )}
                        {selectedCategory === i && selectedSubcategory === -1 && <div style={{ position: "absolute", left: -2, top: -2, right: -2, bottom: -2, border: "3px solid orange", borderRadius: 6 }} />}
                      </div>
                      {/* Render child work items below the category */}
                      <Droppable droppableId={`epic-${item.id}`}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{ 
                              position: 'relative', 
                              width: '100%', 
                              pointerEvents: 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              minHeight: snapshot.isDraggingOver ? 50 : 0,
                            }}
                          >
                            {getSubcategories(i).map((child, childIndex) => {
                          const isSelected = selectedCategory === i && selectedSubcategory === childIndex;
                          
                          // Calculate progress for indicator positioning
                          let progressPercent = 0;
                          if (child.dod_status && Object.keys(doDsByDiscipline).length > 0) {
                            try {
                              const dodStatuses = JSON.parse(child.dod_status);
                              const hidden = dodStatuses._hidden || [];
                              let totalDoDs = 0;
                              let completedDoDs = 0;
                              
                              disciplines.forEach(discipline => {
                                // Skip excluded disciplines
                                if (discipline.excludeFromDoDs === true) return;
                                if (hidden.includes(discipline.id)) return;
                                const disciplineDoDs = doDsByDiscipline[discipline.name] || [];
                                if (disciplineDoDs.length === 0) return;
                                totalDoDs += disciplineDoDs.length;
                                const dodId = dodStatuses[discipline.id];
                                if (dodId) {
                                  const dodOrder = disciplineDoDs.findIndex(d => d.id === dodId);
                                  if (dodOrder !== -1) {
                                    completedDoDs += (dodOrder + 1);
                                  }
                                }
                              });
                              
                              if (totalDoDs > 0) {
                                progressPercent = (completedDoDs / totalDoDs) * 100;
                              }
                            } catch (e) {
                              // If parsing fails, progressPercent stays 0
                            }
                          }
                          
                          return (
                          <Draggable key={child.id} draggableId={`subepic-${child.id}`} index={childIndex}>
                            {(provided, snapshot) => {
                              const getItemStyle = (draggableStyle, isDragging) => {
                                // When dragging, the library provides position: fixed with calculated top/left
                                // With getContainerForClone, the library should handle positioning correctly
                                // We just need to spread draggableStyle FIRST and add our custom styles
                                if (isDragging && draggableStyle) {
                                  // Use the library's calculated position directly - getContainerForClone handles scroll
                                  return {
                                    ...draggableStyle, // Library's positioning (position: fixed, top, left, etc.) - MUST come first
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0,
                                  };
                                }
                                
                                // When not dragging, use position: relative
                                return {
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0,
                                  // Merge any additional styles from the library (should be minimal when not dragging)
                                  ...draggableStyle,
                                };
                              };
                              
                              return (
                          <div 
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(provided.draggableProps.style, snapshot.isDragging)}>
                          {isSelected && <div style={{ position: "absolute", left: -2, top: 2, right: -2, bottom: -2, border: "3px solid orange", borderRadius: 6, zIndex: 2 }} />}
                          <div style={{
                            ...(snapshot.isDragging ? {
                              opacity: 0.8,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                            } : {}),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            flex: 1,
                            position: 'relative',
                            background: (() => {
                              if (!child.dod_status || Object.keys(doDsByDiscipline).length === 0) return '#fff';
                              
                              // Parse dod_status JSON
                              let dodStatuses = {};
                              try {
                                dodStatuses = JSON.parse(child.dod_status);
                              } catch (e) {
                                // If parsing fails, return default
                                return '#fff';
                              }
                              
                              const hidden = dodStatuses._hidden || [];
                              
                              // Calculate total DoDs across all non-hidden disciplines
                              let totalDoDs = 0;
                              let completedDoDs = 0;
                              let allAtFinalDoD = true;
                              let hasAnyDoDStatus = false;
                              
                              disciplines.forEach(discipline => {
                                // Skip excluded disciplines
                                if (discipline.excludeFromDoDs === true) {
                                  return;
                                }
                                // Skip hidden disciplines
                                if (hidden.includes(discipline.id)) {
                                  return;
                                }
                                
                                const disciplineDoDs = doDsByDiscipline[discipline.name] || [];
                                if (disciplineDoDs.length === 0) {
                                  return;
                                }
                                
                                // Add to total DoDs count
                                totalDoDs += disciplineDoDs.length;
                                
                                const dodId = dodStatuses[discipline.id];
                                if (dodId) {
                                  hasAnyDoDStatus = true;
                                  const dodOrder = disciplineDoDs.findIndex(d => d.id === dodId);
                                  if (dodOrder !== -1) {
                                    // dodOrder is 0-indexed, so dodOrder + 1 is the number of DoDs completed
                                    completedDoDs += (dodOrder + 1);
                                    
                                    // Check if this discipline is at final DoD
                                    if (dodOrder !== disciplineDoDs.length - 1) {
                                      allAtFinalDoD = false;
                                    }
                                  } else {
                                    allAtFinalDoD = false;
                                  }
                                } else {
                                  // Discipline has no DoD status, not at final
                                  allAtFinalDoD = false;
                                }
                              });
                              
                              // If no disciplines or no DoD statuses set, return default
                              if (totalDoDs === 0 || !hasAnyDoDStatus) {
                                return '#fff';
                              }
                              
                              // Calculate progress: completed DoDs / total DoDs
                              const aggregatedProgress = completedDoDs / totalDoDs;
                              
                              // If all disciplines are at final DoD, show lime green
                              if (allAtFinalDoD && hasAnyDoDStatus) {
                                return '#32cd32'; // lime green
                              }
                              
                              // Otherwise show progress bar
                              const progressPercent = aggregatedProgress * 100;
                              return `linear-gradient(to right, #81c784 ${progressPercent}%, #fff ${progressPercent}%)`;
                            })(),
                            color: '#111',
                            borderRadius: 5,
                            marginTop: 4, // 3px all around (reduced from 5px by 2px)
                            width: maxCategoryWidth - 8, // Slightly less than category width
                            height: 22,
                            justifyContent: 'space-between', // space between title and icon
                            fontSize: 12,
                            fontWeight: 'normal',
                            boxShadow: '0 1px 4px #0001',
                            padding: '0 10px',
                            cursor: editingChildId === child.id ? 'text' : (snapshot.isDragging ? 'grabbing' : 'grab'),
                            pointerEvents: 'auto', // Re-enable pointer events for individual subcategories
                            overflowX: "hidden",
                          }}
                          onMouseEnter={() => setSubEpicHovered(child.id)}
                          onMouseLeave={() => setSubEpicHovered(null)}
                          onMouseDown={(e) => {
                            // Track initial mouse position to detect if this becomes a drag
                            if (!justDraggedSubEpicRef.current) {
                              justDraggedSubEpicRef.current = false; // Reset on new interaction
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (editingChildId === child.id) return;
                            // Don't open modal if we just finished dragging
                            if (justDraggedSubEpicRef.current) {
                              // Reset immediately after checking
                              setTimeout(() => {
                                justDraggedSubEpicRef.current = false;
                              }, 0);
                              return;
                            }
                            setSelectedCategory(i);
                            setSelectedSubcategory(childIndex);
                            setModalChild(child);
                            setModalChildTitle(child.title);
        setModalChildDesc(child.description || "");
        // Parse dod_status JSON or default to empty object
                            try {
                              const dodStatuses = child.dod_status ? JSON.parse(child.dod_status) : {};
                              const cleanedStatuses = cleanDodStatus(dodStatuses, disciplines);
                              setModalChildDoDStatuses(cleanedStatuses);
                            } catch (e) {
                              // If parsing fails, default to empty object
                              setModalChildDoDStatuses({});
                            }
                            setModalChildDoDEditMode(false);
                            setModalChildError("");
                          }}
                          >
                            {editingChildId === child.id ? (
                              <input
                                type="text"
                                value={childDraft}
                                autoFocus
                                onChange={e => {
                                  if (e.target.value.length <= 50) {
                                    setChildDraft(e.target.value);
                                  }
                                }}
                                onBlur={async () => {
                                  if (!childDraft.trim() && supabase) {
                                    await supabase.from('work').delete().eq('id', child.id);
                                    setWorkItems(ws => ws.filter(w => w.id !== child.id));
                                  } else if (childDraft.trim() && supabase) {
                                    await supabase.from('work').update({ title: childDraft.trim() }).eq('id', child.id);
                                    setWorkItems(ws => ws.map(w => w.id === child.id ? { ...w, title: childDraft.trim() } : w));
                                  }
                                  setEditingChildId(null);
                                  setChildDraft('');
                                }}
                                onKeyDown={async e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!childDraft.trim() && supabase) {
                                      // Delete current sub-category if no text
                                      await supabase.from('work').delete().eq('id', child.id);
                                      setWorkItems(ws => ws.filter(w => w.id !== child.id));
                                      setEditingChildId(null);
                                      setChildDraft('');
                                    } else if (childDraft.trim() && supabase) {
                                      // Save current sub-category and create a new one below
                                      await supabase.from('work').update({ title: childDraft.trim() }).eq('id', child.id);
                                      setWorkItems(ws => ws.map(w => w.id === child.id ? { ...w, title: childDraft.trim() } : w));
                                      
                                      // Create new sub-category below
                                      if (user?.proj?.id && supabase) {
                                        const { data } = await supabase.from('work').insert({
                                          title: '',
                                          type: 'subepic',
                                          parent_id: item.id,
                                          project_id: user.proj.id,
                                          created_by: user.id,
                                          owner_id: user.id
                                        }).select();
                                        if (data && data[0]) {
                                          setWorkItems(prev => [...prev, data[0]]);
                                          setEditingChildId(data[0].id);
                                          setChildDraft('');
                                        }
                                      }
                                    }
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!childDraft.trim() && supabase) {
                                      await supabase.from('work').delete().eq('id', child.id);
                                      setWorkItems(ws => ws.filter(w => w.id !== child.id));
                                    }
                                    setEditingChildId(null);
                                    setChildDraft('');
                                  }
                                }}
                                style={{
                                  width: 100,
                                  border: 'none',
                                  fontSize: 12,
                                  fontWeight: 'normal',
                                  color: '#111',
                                  background: 'transparent',
                                  textAlign: 'center',
                                  outline: 'none',
                                  boxShadow: 'none',
                                  padding: 0,
                                  margin: 0,
                                  height: 16,
                                  lineHeight: 1,
                                  display: 'inline-block',
                                  verticalAlign: 'middle',
                                }}
                              />
                            ) : (
                              <>
                                <div style={{ fontWeight: 500, fontSize: 12, color: '#111', userSelect: 'none', flex: 1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative', zIndex: 1 }}
                                onDoubleClick={() => {
                                  setEditingChildId(child.id);
                                  setChildDraft(child.title);
                                }}
                              >{child.title || <span style={{ color: '#bbb' }}>New Task</span>}</div>
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: 6,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    backgroundColor: breakdownButtonHovered === child.id ? '#e0e0e0' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: subEpicHovered === child.id ? 1 : 0,
                                    transition: 'opacity 0.2s, background-color 0.2s',
                                    pointerEvents: 'auto',
                                    zIndex: 10,
                                  }}
                                  onMouseEnter={() => {
                                    setBreakdownButtonHovered(child.id);
                                  }}
                                  onMouseLeave={() => {
                                    setBreakdownButtonHovered(null);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCategory(i);
                                    setSelectedSubcategory(childIndex);
                                    // Pass the specific child to openEditModal so it opens for this sub-epic, not the highlighted one
                                    openEditModal(true, child);
                                  }}
                                >
                                  <img
                                    src={branchIcon}
                                    alt="breakdown"
                                    style={{
                                      width: 10,
                                      height: 10,
                                      filter: 'brightness(0.7)',
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            {hasActiveTasks(child.id) && (
                              <div 
                                className="work-indicator-bar"
                                style={{
                                  position: 'absolute',
                                  left: `${progressPercent}%`,
                                  top: 0,
                                  
                                }}
                              />
                            )}
                          </div>
                          </div>
                              );
                            }}
                          </Draggable>
                          );
                        })}
                        {provided.placeholder}
                        </div>
                        )}
                      </Droppable>
                        {/* Break down work button - show when sub-epic is selected */}
                        {selectedCategory === i && selectedSubcategory > -1 && (
                          <div style={{ marginTop: 8, marginLeft: 0 }}>
                            <KeyboardShortcut 
                              type="comboenter" 
                              text="Break down work" 
                              onClickCb={() => {
                                const subcategories = getSubcategories(i);
                                if (subcategories[selectedSubcategory]) {
                                  openEditModal(true);
                                }
                              }}
                            />
                          </div>
                        )}
                        {/* Plus button for adding child */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          width: '100%',
                          marginTop: 8,
                          zIndex: 10,
                        }}>
                          <div
                            className="plus-child-btn"
                            style={{
                              opacity: workItems.filter(w => w.type === 'subepic' && w.parent_id != null).length === 0 
                                ? (buttonHovered[item.id] ? 1 : 0.6)  // 60% when no subcategories exist, 100% when button hovered
                                : childAreaHovered[item.id] 
                                  ? (buttonHovered[item.id] ? 1 : 0.6)  // 60% when area hovered, 100% when button hovered
                                  : 0,  // Hidden until area hovered when subcategories exist
                              transition: 'opacity 0.2s',
                              background: 'transparent',
                              borderRadius: '50%',
                              width: 22,
                              height: 22,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                            onMouseEnter={() => setButtonHovered(prev => ({ ...prev, [item.id]: true }))}
                            onMouseLeave={() => setButtonHovered(prev => ({ ...prev, [item.id]: false }))}
                          >
                            <ButtonPlus
                              size={18}
                              style={{ pointerEvents: 'auto' }} // Re-enable pointer events for the button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!user?.proj?.id || !supabase) return;
                                const { data } = await supabase.from('work').insert({
                                  title: '',
                                  type: 'subepic',
                                  parent_id: item.id,
                                  project_id: user.proj.id,
                                  created_by: user.id,
                                  owner_id: user.id
                                }).select();
                                if (data && data[0]) {
                                  setWorkItems(prev => [...prev, data[0]]);
                                  setEditingChildId(data[0].id);
                                  setChildDraft('');
                                }
                              }}
                            />
                          </div>
                        </div>
                    </div>
                  );
                })}
                {/* ButtonPlus always to the right, inline, styled like Timeline */}
                  <div 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginLeft: 8, 
                      height: 36,
                      opacity: addCategoryButtonHovered ? 1 : 0.6,
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={() => setAddCategoryButtonHovered(true)}
                    onMouseLeave={() => setAddCategoryButtonHovered(false)}
                  >
                      <ButtonPlus
                    onClick={async () => {
                      if (!user?.proj?.id || !supabase) return;
                      const { data } = await supabase.from('work').insert({
                        title: '',
                        type: 'epic',
                        parent_id: null,
                        project_id: user.proj.id,
                        created_by: user.id,
                        owner_id: user.id
                      }).select();
                      if (data && data[0]) {
                        setWorkItems(prev => [...prev, data[0]]);
                        setEditingCategoryId(data[0].id);
                        setCategoryDraft('');
                        setShowSuggestions(true);
                      }
                    }}
                        size={24}
                      />
                    </div>
                </div>
                </DragDropContext>
              </div>
            )}
          </div>
      )}
      {/* Only show suggestions if showSuggestions is true and there are categories */}
      {showSuggestions && user?.proj?.description && (
          <div data-suggestions-overlay style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            padding: '20px 32px 0 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(30, 18, 61, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 1000,
            boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ fontWeight: 600, fontSize: 18, color: '#fff', textAlign: 'left', marginLeft: 2, marginBottom: 8 }}>Suggestions</div>
            <SuggestedComponents
              components={filteredSuggestions}
              onAdd={async epic => {
              // If there is an empty category, delete it first
              const emptyCategory = workItems.find(w => w.type === 'epic' && w.parent_id == null && !w.title);
              if (emptyCategory && supabase) {
                await supabase.from('work').delete().eq('id', emptyCategory.id);
                setWorkItems(ws => ws.filter(w => w.id !== emptyCategory.id));
              }
              // Add the suggestion as a new category
                if (user?.proj?.id && supabase && epic?.title) {
                  await supabase.from('work').insert({
                    title: epic.title,
                    description: epic.description || '',
                    type: 'epic',
                    parent_id: null,
                    project_id: user.proj.id,
                    created_by: user.id,
                    owner_id: user.id
                  });
                setLastAddedEpicTitle(epic.title);
                  // Refetch work items
                  const { data } = await supabase
                    .from("work")
                    .select()
                    .eq("project_id", user.proj.id)
                    .order("id", { ascending: true });
                  setWorkItems(data || []);
                // Do NOT hide suggestions here!
            }
          }}
            onSuggestionMouseDown={undefined}
            setHoveredSuggestionPanel={setHoveredSuggestionPanel}
          kept={workItems.filter(w => w.type === 'epic' && w.parent_id == null).map(w => ({ title: w.title }))}
          plusOnly
        />
      </div>
      )}
      <CategoryModal
        modalCategory={modalCategory}
        modalTitle={modalTitle}
        modalDesc={modalDesc}
        modalError={modalError}
        workItems={workItems}
        supabase={supabase}
        setModalCategory={setModalCategory}
        setModalTitle={setModalTitle}
        setModalDesc={setModalDesc}
        setModalError={setModalError}
        setWorkItems={setWorkItems}
      />
      {modalChild && (
        <Modal
          disableEscape={editingTaskId !== null}
          closeCb={async () => {
            // If a task is highlighted, just clear the highlighting instead of closing
            if (selectedTaskIndex !== -1 || highlightedTaskId !== null) {
              setSelectedTaskIndex(-1);
              setHighlightedTaskId(null);
              // Also close search if it's open
              if (modalSearchVisible) {
                setModalSearchVisible(false);
                setModalSearchText("");
                modalTypingBufferRef.current = "";
              }
              return;
            }
            // Save description before closing - capture values before state is cleared
            const childId = modalChild?.id;
            const descToSave = modalChildDesc;
            if (childId) {
              // Clear timer first to prevent duplicate saves
              if (descriptionSaveTimerRef.current) {
                clearTimeout(descriptionSaveTimerRef.current);
              }
              // Save immediately
              try {
                await supabase.from('work').update({ description: descToSave }).eq('id', childId);
                setWorkItems(ws => ws.map(w => w.id === childId ? { ...w, description: descToSave } : w));
              } catch (error) {
                console.error('Error saving description on close:', error);
              }
            }
            setModalChild(null);
            setModalChildDoDEditMode(false);
            setShowBreakdownOptions(false);
            setBreakdownSelectedStory(null);
            setBreakdownNewStoryName("");
          }}
        >
          <div 
            style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 320 }}
            onClick={(e) => {
              // If clicking outside the breakdown options (task creation form)
              if (showBreakdownOptions && breakdownOptionsRef.current && !breakdownOptionsRef.current.contains(e.target)) {
                // Check if click is on interactive elements that should be ignored
                const target = e.target;
                const isInteractiveElement = 
                  target.tagName === 'INPUT' ||
                  target.tagName === 'BUTTON' ||
                  target.tagName === 'TEXTAREA' ||
                  target.closest('button') ||
                  target.closest('input') ||
                  target.closest('textarea') ||
                  target.closest('[style*="z-index: 100"]') || 
                  target.closest('[style*="z-index: 1000"]') ||
                  target.closest('[role="listbox"]') ||
                  target.closest('[role="option"]') ||
                  target.closest('[role="combobox"]');
                
                // If not clicking on interactive elements, handle the click
                if (!isInteractiveElement) {
                  // If there's a task name, create the task; otherwise cancel
                  if (breakdownNewStoryName.trim()) {
                    handleCreateNewTask();
                  } else {
                    // Cancel if no task name
                    setShowBreakdownOptions(false);
                    setBreakdownNewStoryName("");
                    setStoryInputValue("");
                    setNoStorySelected(false);
                    setBreakdownSelectedStoryId(null);
                    setBreakdownTaskEstimate(null);
                    setBreakdownTaskAssignee(null);
                    setBreakdownTaskStatus('TODO');
                  }
                }
              }
            }}
          >
            {/* Inline editable title header */}
            <h2 style={{ color: "#000", marginTop: -11, marginBottom: 0, padding: 0, display: 'flex', alignItems: 'center' }}>
            <Input
              ref={modalChildTitleInputRef}
              value={modalChildTitle}
              onChange={e => setModalChildTitle(e.target.value)}
                placeholder="Sub-Epic title..."
                left
                noborder
                customStyle={{
                  background: "transparent",
                  padding: 0,
                  margin: 0,
                  boxShadow: "none",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  height: "auto",
                  color: "inherit",
                  width: "100%",
                  border: "none",
                  outline: "none",
                }}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setModalChild(null);
                  setModalChildDoDEditMode(false);
                }
              }}
            />
            </h2>
            <div
              style={{
                marginLeft: 12,
                marginRight: 12,
                marginTop: -16, // Remove gap between title and description
                marginBottom: 20,
              }}
            >
              <Textarea
                ref={descriptionTextareaRef}
                value={modalChildDesc}
                onChange={e => {
                  setModalChildDesc(e.target.value);
                }}
                placeholder="Notes here..."
                white
                minHeight={40}
                customStyle={{
                  color: '#111',
                  border: 'none',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 'normal',
                  background: 'transparent',
                  textAlign: 'left',
                  boxShadow: 'none',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  resize: 'none',
                  minHeight: 40, // Override textareaStyle's default minHeight of 200
                  maxHeight: 300, // Limit to 15 rows (14px font * 1.4 line-height * 15 rows ≈ 294px, rounded to 300px)
                  overflow: 'auto',
                }}
                className="modal-notes-dark-placeholder"
              />
            </div>
            <SubEpicDoDSection
              disciplines={disciplines}
              doDsByDiscipline={doDsByDiscipline}
              modalChildDoDStatuses={modalChildDoDStatuses}
              setModalChildDoDStatuses={setModalChildDoDStatuses}
              modalChildDoDEditMode={modalChildDoDEditMode}
              setModalChildDoDEditMode={setModalChildDoDEditMode}
              editDisciplinesButtonHovered={editDisciplinesButtonHovered}
              setEditDisciplinesButtonHovered={setEditDisciplinesButtonHovered}
            />
            {/* Current Work section */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 'bold', color: '#111' }}>Work Items</label>
              </div>
              <SubEpicTaskList
                modalChildTasks={modalChildTasks}
                editingTaskId={editingTaskId}
                setEditingTaskId={setEditingTaskId}
                setEditingTaskField={setEditingTaskField}
                users={users}
                allStories={allStories}
                handleUpdateTaskField={handleUpdateTaskField}
                handleCreateStoryAndAssign={handleCreateStoryForTask}
                handleTaskDragEnd={handleTaskDragEnd}
                selectedTaskIndex={selectedTaskIndex}
                highlightedTaskId={highlightedTaskId}
                showBreakdownOptions={showBreakdownOptions}
                setShowBreakdownOptions={setShowBreakdownOptions}
                modalChild={modalChild}
                breakdownNewStoryName={breakdownNewStoryName}
                setBreakdownNewStoryName={setBreakdownNewStoryName}
                breakdownTaskEstimate={breakdownTaskEstimate}
                setBreakdownTaskEstimate={setBreakdownTaskEstimate}
                breakdownTaskStatus={breakdownTaskStatus}
                setBreakdownTaskStatus={setBreakdownTaskStatus}
                breakdownTaskAssignee={breakdownTaskAssignee}
                setBreakdownTaskAssignee={setBreakdownTaskAssignee}
                breakdownSelectedStoryId={breakdownSelectedStoryId}
                setBreakdownSelectedStoryId={setBreakdownSelectedStoryId}
                breakdownStories={breakdownStories}
                storyInputValue={storyInputValue}
                setStoryInputValue={setStoryInputValue}
                noStorySelected={noStorySelected}
                setNoStorySelected={setNoStorySelected}
                handleCreateNewTask={handleCreateNewTask}
                breakdownOptionsRef={breakdownOptionsRef}
              />
            </div>
            {modalChildError && <div style={{ color: 'red', fontWeight: 500 }}>{modalChildError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'flex-end' }}>
              <button
                style={{ padding: '8px 18px', borderRadius: 5, border: 'none', background: '#27ae60', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                onClick={async () => {
                  if (!modalChildTitle.trim()) {
                    setModalChildError('Title cannot be empty');
                    return;
                  }
                  // Clean up the statuses object: remove empty _hidden array, remove discipline entries with empty values, and remove invalid discipline IDs
                  let cleanedStatuses = cleanDodStatus(modalChildDoDStatuses, disciplines);
                  // Remove empty discipline entries
                  Object.keys(cleanedStatuses).forEach(key => {
                    if (key !== '_hidden' && (!cleanedStatuses[key] || cleanedStatuses[key] === '')) {
                      delete cleanedStatuses[key];
                    }
                  });
                  // Remove _hidden if it's empty
                  if (cleanedStatuses._hidden && cleanedStatuses._hidden.length === 0) {
                    delete cleanedStatuses._hidden;
                  }
                  // Only save if there's at least one discipline status or hidden disciplines
                  const dodStatusJson = Object.keys(cleanedStatuses).length > 0 
                    ? JSON.stringify(cleanedStatuses) 
                    : null;
                  
                  await supabase.from('work').update({ 
                    title: modalChildTitle.trim(), 
                    description: modalChildDesc,
                    dod_status: dodStatusJson
                  }).eq('id', modalChild.id);
                  setWorkItems(ws => ws.map(w => w.id === modalChild.id ? { 
                    ...w, 
                    title: modalChildTitle.trim(), 
                    description: modalChildDesc,
                    dod_status: dodStatusJson
                  } : w));
                  setModalChild(null);
                  setModalChildDoDEditMode(false);
                }}
              >Save</button>
              {/* Only allow delete if no children */}
              {workItems.filter(w => w.parent_id === modalChild.id).length === 0 && (
                <button
                  style={{ padding: '8px 18px', borderRadius: 5, border: 'none', background: '#e74c3c', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                  onClick={async () => {
                    await supabase.from('work').delete().eq('id', modalChild.id);
                    setWorkItems(ws => ws.filter(w => w.id !== modalChild.id));
                    setModalChild(null);
                  }}
                >Delete</button>
              )}
          </div>
        </div>
        </Modal>
      )}

      {/* Search component for modal - rendered in portal to appear above modal */}
      {modalChild && (() => {
        const modalRoot = document.getElementById("modal-root");
        if (!modalRoot || !modalSearchVisible) return null;
        
        return ReactDOM.createPortal(
          <Search
            isVisible={modalSearchVisible}
            onClose={() => {
              setModalSearchVisible(false);
              setModalSearchText("");
              setHighlightedTaskId(null);
              modalTypingBufferRef.current = "";
            }}
            onSearch={handleModalSearch}
            onTextChange={setModalSearchText}
            placeholder="Search tasks..."
            initialText={modalSearchText}
          />,
          modalRoot
        );
      })()}
    </div>
  );
} 